/* ---------------------------------------------------------------------------
   ws-cart.js — WELLX cart drawer controller, ported from SNOX onto Impact 7.0.1.
   Authority: docs/reference/snox/WS_Cart_Impulse_Master_Spec.md.

   ONE controller (spec 0.3). Zero edits to Impact's own JS. Everything reads
   from #ws-cart-config and never re-derives config from scattered DOM (0.4).

   HOW THIS SITS ON IMPACT
   Impact replaces .cart-drawer__inner's innerHTML on every cart change
   (theme.js:1807-1815), on a 1250ms timer for quantity and remove and
   immediately for variant:add. That wipes anything we injected, so we observe
   the node and re-apply after each swap rather than assuming our DOM survives.

   Spec 3 says keep the host theme's native line-item mechanics. We do: the
   <line-item> elements, their stepper and their remove control are Impact's.
   We restyle around them. We never issue the quantity request ourselves, so
   Impact's pill-loader and checkmark stay the in-progress signal for the line.

   TIMING (from the audit, and it is Impact's own split, not ours)
   Impact replaces [slot="footer"] synchronously but delays the line items by
   1250ms, which is exactly the PillLoader success timeline. So:
     - totals-derived WS content updates immediately on cart:change
     - line-scoped WS content rides the swap
   Do not invent a third timing.
   --------------------------------------------------------------------------- */

(function () {
  'use strict';

  var CONFIG_ID = 'ws-cart-config';
  var DRAWER_SELECTOR = 'cart-drawer';
  var INNER_SELECTOR = '.cart-drawer__inner';

  /* Spec 1.1: ~250ms. This collapses rapid clicks into one reconcile pass. It
     does NOT arbitrate ordering — see seq below. */
  var RECONCILE_DEBOUNCE_MS = 250;

  var config = null;

  /* Spec 1.2: set synchronously before the first network await, cleared after
     the final broadcast, so the pass does not re-trigger itself. */
  var mutating = false;

  /* Spec 1.3: queues, never drops. A signal arriving mid-pass schedules exactly
     one follow-up pass, no interleaving. */
  var passQueued = false;
  var debounceTimer = null;

  /* Race arbitration. Impact never calls clearTimeout on its 1250ms swap
     (theme.js:1770-1860) and never aborts an in-flight request
     (theme.js:2038-2046), so stale swaps land after fresh ones by construction.
     theme.js is a delivered asset and off limits, so we arbitrate from our side:
     every local intent carries a sequence number and the newest is re-applied
     after any swap that arrives stale. */
  var intentSeq = 0;

  /* Mutations one reconcile pass may perform before deferring the rest to the
     next one. Sized well above any honest cart and well below a hang. */
  var ACTION_BUDGET = 24;
  var appliedSeq = -1;

  /* Captured before a swap, restored after. Impact resets scrollTop to zero
     because innerHTML assignment destroys and recreates the children, and there
     is no scroll restoration anywhere in its CartDrawer class. */
  var savedScrollTop = 0;

  var observer = null;

  // -------------------------------------------------------------------------
  // Config
  // -------------------------------------------------------------------------

  function readConfig() {
    var el = document.getElementById(CONFIG_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      /* Spec 0.6: degrade silently, never a console error the merchant sees.
         A malformed blob means the WS layer stays off and Impact's own drawer
         keeps working, which is the same outcome as ws_cart_enabled being off. */
      return null;
    }
  }

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  function drawer() {
    return document.querySelector(DRAWER_SELECTOR);
  }

  function inner() {
    var d = drawer();
    return d ? d.querySelector(INNER_SELECTOR) : null;
  }

  /* THE CANONICAL COUNT RULE. Every count surface in the theme goes through
     this one function: the drawer title, the subtotal label, the drawer's bag
     badge, the header bubble and the cart page.

     It counts what the SHOPPER manages — the sum of line quantities, less the
     engine's gift lines and the shipping-protection line. A EUR 0 promotional
     line such as a BOGO free item still counts, because the shopper controls
     it and can remove it.

     Deliberately not Shopify's cart.item_count, which counts the gift and the
     protection and so disagreed with the drawer: header 4, drawer 2 items, on
     one cart. snippets/ws-visible-count.liquid states the same rule for the
     server render — see its comment for why the two identify the protection
     line by different evidence. */
  function wsVisibleCount(cart) {
    if (!cart || !cart.items) return 0;
    return cart.items.reduce(function (total, item) {
      /* Same classification the threshold maths uses, so the header count and
         the bar can never disagree about what a gift or protection line is. */
      if (isGiftLine(item) || isProtectionLine(item)) return total;
      return total + item.quantity;
    }, 0);
  }

  /* Plural selection goes through Intl.PluralRules for the active locale, not
     `count === 1`. This theme ships 28 locales and several of them — ar, pl, ru,
     cs, lt — have more than two CLDR categories, so a one/other ternary states
     the wrong form for them. The locale file supplies whichever categories it
     defines; anything it does not define falls back to "other", which is what
     Liquid's own `t` filter returns for those counts anyway. */
  function itemCountLabel(count, which) {
    var forms = (config && config[which || 'itemCount']) || {};
    var category = 'other';

    try {
      category = new Intl.PluralRules(config.locale || 'en').select(count);
    } catch (e) {
      /* No Intl, or an unrecognised locale tag. "other" is the safe form. */
    }

    var template = forms[category] || forms.other;
    if (!template) return '';

    /* [count], not {{ count }}. Liquid's `t` filter substitutes {{ count }} at
       harvest time, so a template harvested with `t: count: 5` arrives as
       "5 items in cart" with the 5 already baked in and nothing left to
       replace. Verified in a browser: a 3-item cart displayed "5 items in
       cart". [count] passes through `t` untouched while the count param still
       drives plural selection. */
    return template.replace(/\[count\]/g, count);
  }

  // -------------------------------------------------------------------------
  // Paint
  // -------------------------------------------------------------------------

  /* Colours come from the blob, never hardcoded (spec 0.5), and land as custom
     properties on the WS root. Never on :root — that would restyle the store. */
  /* Design tokens are emitted in Liquid by sections/ws-cart-drawer.liquid, the
     same way the live SNOX drawer does it. They are deliberately NOT set here.

     This function used to write them onto each element after first paint, which
     cost a repaint on every cart change and — worse — wrote --ws-card,
     --ws-secondary and --ws-emphasis while the stylesheet read --ws-sec,
     --ws-emph and no card token, so three merchant colour settings did nothing.

     It stays as a marker rather than disappearing: both call sites are the
     points where drawer-owned elements enter the DOM, which is where any future
     per-element token would have to be applied. Removing it would lose that. */
  function applyTokens(root) {
    if (root && !root.hasAttribute('data-ws-tokens')) {
      root.setAttribute('data-ws-tokens', 'liquid');
    }
  }

  /* The reference's trash icon, reproduced attribute for attribute rather than
     routed through svgIcon(). It differs from every other icon here in three
     ways that svgIcon() cannot express: ONE path carrying all four subpaths
     (not one <path> each), stroke-linecap="square", and stroke-linejoin="arcs".

     `arcs` is an SVG2 value no browser implements today — it resolves to miter.
     It is kept because the reference carries it and the brief said verbatim;
     it is inert either way, and if browsers ever ship it this matches without
     a change.

     No stroke/fill/size attributes: those come from CSS so the icon inherits
     currentColor and one sizing rule, the same as the rest of the drawer. */
  function trashIcon() {
    var NS = 'http://www.w3.org/2000/svg';
    var svg = document.createElementNS(NS, 'svg');
    svg.setAttribute('xmlns', NS);
    svg.setAttribute('stroke-linecap', 'square');
    svg.setAttribute('stroke-linejoin', 'arcs');
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('class', 'icon-theme icon-theme-stroke icon-core-trash');
    svg.setAttribute('viewBox', '0 0 24 24');
    var path = document.createElementNS(NS, 'path');
    path.setAttribute('d', 'm19.5 4-1.072 18H5.571L4.5 4M10 9v8m4-8v8M3 4h18M9 4V2h6v2');
    svg.appendChild(path);
    return svg;
  }

  function svgIcon(d, size, stroke) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 24 24');
    svg.setAttribute('width', size);
    svg.setAttribute('height', size);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', stroke || '1.6');
    svg.setAttribute('stroke-linecap', 'round');
    svg.setAttribute('stroke-linejoin', 'round');

    /* d may be one string or an array of them. The reference draws several
       icons as separate <path> elements, and a parity check that compares the
       rendered path list sees "three paths" against "one path with three M
       subpaths" as a difference even though they stroke identically. Emitting
       one <path> per entry keeps the DOM the same shape as the reference. */
    var ds = Object.prototype.toString.call(d) === '[object Array]' ? d : [d];
    ds.forEach(function (entry) {
      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', entry);
      svg.appendChild(path);
    });
    return svg;
  }

  /* The rewards bar is the one module built as an HTML string rather than by
     createElement, because each node and label carries an inline left/max-width
     that only makes sense as part of one atomic write. Everything interpolated
     into it is merchant copy or a config id, so both escapes are mandatory. */
  function escapeHtml(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;');
  }

  function escapeAttr(s) {
    return escapeHtml(s).replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  var ICON = {
    /* Verbatim from WELLX_Cart_Drawer.html:261 — the three paths of the
       reference's bag, in its order. The previous single-path bag was a
       different glyph entirely and was the visible mismatch in the header. */
    bag: [
      'M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z',
      'M3 6h18',
      'M16 10a4 4 0 0 1-8 0'
    ],
    truck: 'M2 6h11v9H2zM13 9h4l3 3v3h-7zM6 18.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3zM17 18.5a1.5 1.5 0 100-3 1.5 1.5 0 000 3z',
    gift: 'M3 9h18v11H3zM3 9l2-4h14l2 4M12 5v15',
    lock: 'M7 11V8a5 5 0 0110 0v3M5 11h14v9H5z',
    /* discount tag, for the band's Item Discounts row */
    tag: 'M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82zM7 7.01l.01-.011',
    arrow: 'M4 12h15M13 6l6 6-6 6',
    /* Open-box icon, WELLX_Cart_Drawer.html:338 (protection tile) and :351
       (upsell heading) — same three strokes as the reference's two <path>s,
       combined into one d string. A single <path> can hold multiple M...
       subpaths; stroke-only rendering (fill:none everywhere in this file)
       makes that exactly equivalent to separate <path> elements. */
    box: 'M21 8v8l-9 5-9-5V8l9-5zM3 8l9 5 9-5M12 13v8'
  };

  /* Sentence case, with a bag icon and a count badge on its top-left corner.
     The target changed this from the earlier reference's uppercase,
     letter-spaced title and names both changes explicitly. */
  function buildHeader(count) {
    var strings = (config && config.strings) || {};
    var header = document.createElement('div');
    header.className = 'ws-cart__header';

    var bag = document.createElement('span');
    bag.className = 'ws-cart__bag';
    bag.appendChild(svgIcon(ICON.bag, '26'));

    var badge = document.createElement('span');
    badge.className = 'ws-cart__bag-count';
    badge.setAttribute('data-ws-badge', '');
    badge.textContent = count;
    bag.appendChild(badge);

    var text = document.createElement('div');
    text.className = 'ws-cart__header-text';

    var title = document.createElement('p');
    title.className = 'ws-cart__title';
    title.textContent = strings.title || '';

    var subline = document.createElement('p');
    subline.className = 'ws-cart__subline';
    subline.setAttribute('data-ws-subline', '');
    subline.textContent = itemCountLabel(count);

    text.appendChild(title);
    text.appendChild(subline);
    header.appendChild(bag);
    header.appendChild(text);
    return header;
  }

  /* Apply the WS layer to whatever Impact has just rendered. Idempotent: safe to
     call on first load and after every swap. */
  function apply(cart) {
    var innerEl = inner();
    if (!innerEl) return;

    innerEl.classList.add('ws-cart', 'ws-cart__surface');
    applyTokens(innerEl);

    /* Spec 9: the order-level compare-at callout was removed from SNOX by client
       decision and for legal safety, with "do not rebuild it" written into the
       spec. Impact renders that exact callout at cart-drawer.liquid:514 from
       compare-at arithmetic at :473-481. Remove it rather than restyle it. */
    var offerTop = innerEl.querySelector('.cart-offer-top');
    if (offerTop) offerTop.remove();

    /* Impact's own free shipping bar occupies the WS rewards bar slot and one of
       them goes. Its discount-aware arithmetic is reused in the WS threshold
       calculation; the component itself does not survive. */
    var shippingBar = innerEl.querySelector('free-shipping-bar');
    if (shippingBar) shippingBar.remove();

    restyleLineItems(innerEl, cart);

    var count = wsVisibleCount(cart);

    /* RE-STAMP AFTER THE SWAP. Impact's bag badge lives INSIDE
       .cart-drawer__inner (cart-drawer.liquid:522), the element Impact
       reassigns wholesale on every cart change — so each swap destroys the
       claimed element and drops in a fresh one from the Section Rendering API.
       The stamp on cart:change fires before that swap and cannot survive it,
       which left the drawer's badge reading the pre-change number while the
       header, title and subtotal all read the new one. apply() is the
       swap-driven pass, so the re-stamp and re-claim belong here. */
    stampCartCounts(count);

    var existingHeader = innerEl.querySelector('.ws-cart__header');
    if (!existingHeader) {
      existingHeader = buildHeader(count);
      innerEl.insertBefore(existingHeader, innerEl.firstChild);
    }

    /* Impact renders its own header (.cart-drawer__top: "Cart (N)" plus the
       close control). Ours went in above it and both were showing, with the
       rewards bar sandwiched between them — two headers, wrong order.

       The close control cannot simply be hidden with it: it is a customised
       built-in (is="close-button") wired to Impact's drawer, and rebuilding one
       would mean reimplementing that behaviour. So it MOVES into our header,
       same element, same behaviour — the pattern already used for the checkout
       submit — and only then is Impact's header suppressed. */
    var impactTop = innerEl.querySelector('.cart-drawer__top');
    if (impactTop && !impactTop.classList.contains('ws-cart__superseded')) {
      var nativeClose = impactTop.querySelector('[is="close-button"], button[is]');
      if (nativeClose && !existingHeader.querySelector('[is="close-button"], button[is]')) {
        nativeClose.classList.add('ws-cart__close');

        /* The reference draws the close as a × character at 22px/300
           (WELLX_Cart_Drawer.html:57-62, :267), not as an icon. Impact's own
           SVG is hidden in CSS rather than removed, and the glyph is added
           beside it, so this stays the exact element Impact wired to the
           drawer. Impact's .sr-only label is deliberately left in place: the
           reference gets its accessible name from an aria-label instead, and
           dropping the label to match the markup letter-for-letter would cost
           the button its name for no visual gain. */
        if (!nativeClose.querySelector('.ws-cart__close-glyph')) {
          var closeGlyph = document.createElement('span');
          closeGlyph.className = 'ws-cart__close-glyph';
          closeGlyph.setAttribute('aria-hidden', 'true');
          closeGlyph.textContent = '×';
          nativeClose.appendChild(closeGlyph);
        }

        existingHeader.appendChild(nativeClose);
      }
      impactTop.classList.add('ws-cart__superseded');
    }
    updateCount(count);
    renderRewards(cart);
    renderBogoNoteInDrawer(cart);
    renderFooter(cart);

    /* CLAIM THE DRAWER — but only once it has been drawn from REAL cart data.

       apply(null) runs at DOMContentLoaded so the shell is ready early, and it
       does everything that needs no cart: tokens, header, the moved pricing,
       the trash icon. What it CANNOT do is the half that reads the cart — the
       "Size: S/M" line, the discount chip, the gift label, the upsell rail.

       Revealing on that pass showed a drawer that was ours but half-dressed,
       and then popped the missing parts in when the cart landed. That is what
       still read as "the old cards": not Impact's drawer, but ours before it
       had anything to describe.

       So the reveal waits for a pass with a cart behind it. primeCart below
       makes that pass happen immediately rather than after load plus an idle
       callback, and reveals unconditionally if the fetch fails — a drawer that
       never appears would be far worse than one that appears plain.

       Idempotent, and re-set on every pass: Impact swaps this subtree wholesale
       and a re-render that dropped the class would hide the cart. */
    if (cart) {
      var host = drawer();
      if (host) host.classList.add('ws-cart-ready');
    }

    /* The cart page is a second surface for the same cart, driven from here so
       it repaints on exactly the passes the drawer does — a quantity change, a
       removal, a gift appearing — with no second scheduler. Wrapped: /cart must
       never be able to break the drawer, which is the surface that sells. */
    try { applyCartPage(cart); } catch (e) { /* deliberately swallowed */ }

    appliedSeq = intentSeq;
  }

  /* The save chip. Value comes ONLY from a real discount Shopify already
     applied to the line — line_level_discount_allocations, the same field the
     delivered line-item.liquid reads at :97-105. NEVER compare-at arithmetic:
     that is forbidden by the brief and by legal review, and it is also just
     wrong here — compare-at is a catalogue fact about the variant, not proof a
     discount actually landed on this line.

     Not sourced from Impact's own .cart-savings-2 (delivered; suppressed in
     ws-cart.css): that div is gated by `unless line_item.final_price > 1` in
     the delivered snippet, which is never true for a normally priced line, so
     it never renders for real discounts. Fixing that gate would mean editing
     snippets/line-item.liquid, outside this task's files, so the chip is
     built here instead, from the same cart JSON the reconcile pass already
     fetches. */
  function buildSaveChip(item) {
    var allocations = item && item.line_level_discount_allocations;
    if (!allocations || !allocations.length) return null;

    /* A BOGO-style application (verified live: Shopify's own "Buy 1, Get 1
       FREE") attaches a discount_allocation to BOTH the free unit's line and
       the paying unit's line — the paying line's entry carries amount: 0.
       That is real data too, but it is not a real discount ON THIS LINE: the
       paying line saved nothing, so a chip there would claim a saving that
       did not happen on that row. Only an allocation with a positive amount
       is a real discount on the line the brief means. */
    var titles = allocations
      .filter(function (a) { return a && a.amount > 0; })
      .map(function (a) {
        return a.discount_application && a.discount_application.title;
      })
      .filter(function (t) { return !!t; });
    if (!titles.length) return null;

    /* The amount is the sum of the REAL allocations on this line — what Shopify
       actually took off — never compare-at arithmetic. */
    var saved = allocations.reduce(function (sum, a) {
      return sum + ((a && a.amount > 0) ? a.amount : 0);
    }, 0);

    var chip = document.createElement('span');
    chip.className = 'ws-cart__save-chip';

    var icon = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    icon.setAttribute('viewBox', '0 0 24 24');
    icon.setAttribute('fill', 'none');
    icon.setAttribute('stroke', 'currentColor');
    icon.setAttribute('stroke-width', '1.8');
    icon.setAttribute('stroke-linecap', 'round');
    icon.setAttribute('stroke-linejoin', 'round');
    icon.setAttribute('aria-hidden', 'true');
    icon.setAttribute('focusable', 'false');
    var tag = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    tag.setAttribute('d', 'M20.59 13.41 13.42 20.58a2 2 0 0 1-2.83 0L2 12V2h10l8.59 8.59a2 2 0 0 1 0 2.82z');
    var dot = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    dot.setAttribute('cx', '7'); dot.setAttribute('cy', '7'); dot.setAttribute('r', '1.3');
    icon.appendChild(tag); icon.appendChild(dot);
    chip.appendChild(icon);

    var text = titles.join(', ');
    if (saved > 0) text += ' (-' + formatMoney(saved) + ')';
    chip.appendChild(document.createTextNode(text));
    return chip;
  }

  /* Task 2: the gift line. Gated ONLY on the cart line's price genuinely being
     zero (spec decision) — never on a marker, a line property or a tier id.
     That is deliberately looser than isGiftLine() above: isGiftLine() also
     requires a marker or a configured gift-product match because it feeds the
     reward-tier threshold maths, where a false positive would corrupt
     eligibility for every buyer. This is purely cosmetic — a line that reached
     $0 some other way still earns the "Free gift" label, and the brief is
     explicit that price alone is the gate. Uses final_line_price, the same
     field isGiftLine() reads, so the two can never disagree about what "zero"
     means for a line. */
  /* A line carrying a real discount allocation is a DISCOUNTED PURCHASE, not a
     tier gift — a BOGO free unit is the buyer's own product at 100% off. The
     card design shows exactly that: the €0,00 line keeps its "Size: S/M" and
     explains itself with the discount chip, rather than being relabelled "Free
     gift". So the gift label is reserved for a line that is free for some other
     reason (an intrinsically zero-priced gift product). */
  function hasRealLineDiscount(item) {
    return !!item && (item.line_level_discount_allocations || [])
      .some(function (a) { return a && a.amount > 0; });
  }

  /* The label follows the MARKER, with the price as a sanity check: a
     misconfigured "gift" that is not actually free must not be labelled free.
     A line that is zero only because of a real discount is a purchase and keeps
     its variant text instead (see the design supplied 25 August). */
  function isZeroPriceLine(item) {
    return isGiftLine(item) &&
      (item.final_line_price || 0) === 0 &&
      !hasRealLineDiscount(item);
  }

  /* Where the variant text normally sits (WELLX_Cart_Drawer.html:131-135). The
     native variant paragraph (`.text-sm.text-subdued`, line-item.liquid:49)
     only renders when the line carries a real, non-default variant — the same
     condition line-item.liquid:48 tests via has_only_default_variant, exposed
     on the cart JSON item as `variant_title` (null for a default-variant
     line). A single-SKU gift product usually has no such variant, so most of
     the time this INSERTS the label rather than replacing anything.

     When a variant paragraph does exist, it is always the FIRST `.text-subdued`
     under the title, ahead of any selling-plan line (line-item.liquid:49-53),
     so querying by variant_title truthiness first is what makes picking "the
     first one" safe rather than a guess.

     Built as `.ws-cart__gift-label` alone, not `.text-subdued` — that native
     class's higher-specificity rule (`.ws-cart .line-item__info .text-subdued`)
     would win over a same-name colour/size override and silently revert the
     label to grey 12.5px/400. This element is fully ours, so a plain,
     dedicated class is the whole style contract. */
  /* "Size: S/M" — the option NAME is part of the card design, so it is built
     from the line's options_with_values rather than the bare variant title
     Impact prints. Multi-option products join with a middot, which is what the
     variant title does anyway. Falls back to the native text when the cart JSON
     has no options (a line rendered before the fetch resolves). */
  function applyVariantLabel(li, item) {
    var leftH = li.querySelector('.line-item-left-h');
    if (!leftH) return;
    var native = leftH.querySelector('.text-subdued');
    if (!native || !item) return;
    if (native.getAttribute('data-ws-variant') === 'done') return; // idempotent

    var opts = item.options_with_values || [];
    if (!opts.length) return;

    native.textContent = '';
    opts.forEach(function (opt, i) {
      if (i > 0) native.appendChild(document.createTextNode(' \u00B7 '));
      var label = document.createElement('span');
      label.className = 'ws-cart__variant-label';
      /* Some products in this store name the option "Size:" (with the colon
         baked into the option name in admin) and others "Size". Appending a
         colon blindly rendered "Size:: L/XL" on the former, so any trailing
         colon is stripped before exactly one is added back. Cosmetic here; the
         underlying data is worth tidying in admin, since the product page shows
         the same name. */
      label.textContent = String(opt.name || '').replace(/\s*:+\s*$/, '') + ':';
      var value = document.createElement('span');
      value.className = 'ws-cart__variant-value';
      value.textContent = ' ' + opt.value;
      native.appendChild(label);
      native.appendChild(value);
    });
    native.setAttribute('data-ws-variant', 'done');
  }

  function applyGiftLabel(li, item) {
    var leftH = li.querySelector('.line-item-left-h');
    if (!leftH) return;

    var existingLabel = leftH.querySelector('.ws-cart__gift-label');

    if (!isZeroPriceLine(item)) {
      if (existingLabel) existingLabel.remove();
      return;
    }

    if (existingLabel) return; // idempotent: already applied before the next swap

    var label = document.createElement('p');
    label.className = 'ws-cart__gift-label';
    /* Sourced from config.strings, like every other user-facing string in
       this controller (spec 0.7 / 28 shipped locales) — never a hardcoded
       English literal. Reuses ws_cart.rewards.label.gift, the existing
       rewards-bar tier-label key: it already ships "Free gift" across all 28
       locale files, and this is the same phrase in the same language, so one
       translated string serves both surfaces. The '' fallback matches the
       degrade-silently pattern used everywhere else config.strings is read
       (e.g. buildHeader's strings.title || ''): a malformed config blob means
       the WS layer stays off, not a console error the merchant sees. */
    label.textContent = (config && config.strings && config.strings.giftLabel) || '';

    var nativeVariant = item.variant_title ? leftH.querySelector('.text-subdued') : null;
    if (nativeVariant) {
      nativeVariant.replaceWith(label);
    } else {
      var title = leftH.querySelector('.title-cart-drawer');
      if (title) title.insertAdjacentElement('afterend', label);
      else leftH.insertBefore(label, leftH.firstChild);
    }
  }

  /* Task 3: transforms the SAME delivered <line-item> Impact rendered for the
     protection fee into the protect-card look (WELLX_Cart_Drawer.html:
     137-151) — the ink tile replaces the product photo, the title becomes
     our copy, the stepper is hidden (spec 5: exactly one unit, always) and
     the native remove control becomes the "Remove" text link. Nothing is
     duplicated: the remove <a> keeps its original DOM position inside
     <line-item-quantity> so Impact's own click handling
     (LineItemQuantity._onRemoveLinkClicked, theme.js:2032-2039) still fires
     on it — only its class and its text change, same MOVE-not-duplicate
     pattern as the checkout button and the close control. Idempotent via the
     .ws-cart__line--protect marker, because this runs again after every
     swap. */
  /* The protection row's media. It is the PRODUCT'S OWN image, fetched from the
     config blob, exactly like every other line — there is no theme-side graphic
     for this product any more. Swapping the artwork is an admin job.

     The drawn tile survives ONLY as a fallback for a protection product with no
     image set, so the row never collapses to a bare text line. Reaching it means
     the product needs an image uploaded, not that the code needs changing; the
     wrapper is marked so that state is visible in the DOM rather than silent. */
  function protectionMedia(wrapper) {
    var p = (config && config.protection) || {};
    if (p.image) {
      var img = document.createElement('img');
      img.className = 'ws-protect__img';
      img.src = p.image;
      img.alt = '';
      img.loading = 'lazy';
      wrapper.appendChild(img);
      wrapper.classList.remove('ws-protect__media--tile');
      return true;
    }
    wrapper.classList.add('ws-protect__media--tile');
    wrapper.appendChild(svgIcon(ICON.box, '24', '1.4'));
    wrapper.appendChild(buildProtectionBadge());
    return false;
  }

  function applyProtectionCard(li, pricing) {
    if (!li.classList.contains('ws-cart__line--protect')) {
      li.classList.add('ws-cart__line--protect');

      var wrapper = li.querySelector('.line-item__media-wrapper');
      if (!wrapper) {
        /* line-item.liquid only renders this wrapper when line_item.image is
           present. The protection product should carry one, but this covers
           the case it does not rather than silently dropping the tile. */
        wrapper = document.createElement('div');
        wrapper.className = 'line-item__media-wrapper';
        li.insertBefore(wrapper, li.firstChild);
      }
      /* Impact already rendered the product's image into this wrapper. Leave
         it: this line used to have the tile appended on top of it, which meant
         the protection product was the one line in the drawer NOT showing its
         own artwork. Media is drawn here only when Impact rendered none. */
      if (!wrapper.querySelector('img')) protectionMedia(wrapper);

      var titleEl = li.querySelector('.title-cart-drawer');
      if (titleEl) titleEl.textContent = protectionTitleText();

      var removeLink = li.querySelector('.remove-quan-cart');
      if (removeLink) {
        removeLink.classList.add('ws-protect__link');
        removeLink.textContent = (config.strings && config.strings.protectionRemove) || '';
        /* Removal goes through Impact's own control, so this is where an
           explicit opt-out is recorded. Without it the very next reconcile
           re-adds the paid line the buyer just removed. Listener, not a
           handler: Impact's navigation still does the actual removal. */
        removeLink.addEventListener('click', function () {
          setProtectionOptedOut(true);
        });
      }
    }

    /* Price becomes a sibling of the tile and the info column, not a
       stepper passenger — there is no stepper on this card
       (WELLX_Cart_Drawer.html:151, .ws-line--protect .ws-line__price). */
    if (pricing && pricing.parentElement !== li) li.appendChild(pricing);
  }

  /* Task 3: the "Add protection" state, when the fee is not currently a cart
     line. Built as a real <line-item> — the same custom element tag and the
     same delivered classes Impact itself renders for every other line (see
     docs/ws-touchpoints.md #11) — so every CSS rule above applies to it with
     no separate ruleset, satisfying the "one base card, modifiers only" rule.
     Removed the instant either the gate closes or the fee is already a real
     cart line (that line gets applyProtectionCard above instead — never
     both at once). */
  /* ONE controller-owned row that carries BOTH states.

     Impact never renders a line-item for the protection product: its loop skips
     anything tagged `shipping` (sections/cart-drawer.liquid:539), and the
     protection product carries that tag so it does not affect shipping rates.
     So there is no delivered line for applyProtectionCard to restyle, and
     without this row the buyer is charged the fee with nothing on screen at all.

     That matches the intended model anyway — the branded row IS the protection
     UI, and the raw line stays hidden in the drawer. applyProtectionCard is kept
     for the case where a merchant's protection product is NOT tagged `shipping`
     and Impact does render a line; the two are mutually exclusive below. */
  function renderProtectionPrompt(innerEl, cart) {
    var items = (cart && cart.items) || [];
    var p = (config && config.protection) || {};
    var existing = innerEl.querySelector('[data-ws-protection-add]');

    /* "protected" derives from the configured product being in the cart, not
       from the marker, so the row reads Remove immediately even for a line
       adoption has not marked yet. */
    var isProtected = items.some(isProtectionLine);

    /* Visibility mirrors enforcement: the row is hidden exactly when the engine
       would strip the line. The last clause is the one non-obvious case — once
       the buyer has opted out, the re-add link only reappears if the merchant
       enabled it. */
    var visible = protectionGateOpen() && hasPaidLine(cart) &&
      (isProtected || !protectionOptedOut() || !!p.showReadd);

    /* If a real line-item exists, applyProtectionCard owns the UI and this row
       would duplicate it. */
    var rawLine = innerEl.querySelector(
      'line-item.line-item.ws-cart__line--protect:not([data-ws-protection-add])'
    );

    if (!visible || rawLine) {
      if (existing) existing.remove();
      return;
    }

    var row = existing;
    if (!row) {
      row = document.createElement('line-item');
      row.className = 'line-item ws-cart__line--protect';
      row.setAttribute('data-ws-protection-add', '');

      var wrapper = document.createElement('div');
      wrapper.className = 'line-item__media-wrapper';
      protectionMedia(wrapper);
      row.appendChild(wrapper);

      var info = document.createElement('div');
      info.className = 'line-item__info';

      var leftH = document.createElement('div');
      leftH.className = 'line-item-left-h';
      var title = document.createElement('span');
      title.className = 'title-cart-drawer bold';
      title.textContent = protectionTitleText();
      leftH.appendChild(title);
      info.appendChild(leftH);

      var qty = document.createElement('line-item-quantity');
      var span = document.createElement('span');
      span.className = 'text-xs';
      var link = document.createElement('button');
      link.type = 'button';
      link.className = 'ws-protect__link';
      link.addEventListener('click', onProtectionToggleClick);
      span.appendChild(link);
      qty.appendChild(span);
      info.appendChild(qty);

      row.appendChild(info);

      var pricing = document.createElement('div');
      pricing.className = 'line-item-pricing';
      var price = document.createElement('span');
      price.className = 'current-price';
      pricing.appendChild(price);
      row.appendChild(pricing);

      var container = moduleContainer(innerEl);
      container.appendChild(row);
    }

    /* Mutable parts, refreshed every pass. */
    row.setAttribute('data-ws-protected', isProtected ? '1' : '0');
    var linkEl = row.querySelector('.ws-protect__link');
    if (linkEl) {
      linkEl.textContent = isProtected
        ? ((config.strings && config.strings.protectionRemove) || '')
        : ((config.strings && config.strings.protectionAdd) || '');
    }
    var priceEl = row.querySelector('.current-price');
    /* Always the product's REAL price — never faked, never "FREE". */
    if (priceEl) priceEl.textContent = formatMoney(p.price || 0);
  }

  /* REMOVING A LINE MUST NOT LEAVE THE PAGE.

     The delivered remove control is a plain anchor:
         <a href="/cart/change?id=<key>&quantity=0" class="remove-quan-cart link">
     with no JavaScript behind it. `remove-quan-cart` is a previous developer's
     class, not Maestrooo's, so none of Impact's own cart handlers bind to it —
     clicking it was a real navigation to /cart/change, which Shopify answers
     with a redirect to /cart. The drawer did not "close": the whole page went.

     Intercepted here rather than by editing the delivered markup, and routed
     through the same cartChange + broadcastCartChange pair the protection row
     already uses, so Impact re-renders the drawer in place and the totals and
     rewards bar update with it.

     Delegated on the document with capture, because the anchor is inside a
     subtree Impact replaces wholesale on every cart change — a listener bound
     to the element itself would survive exactly one render. */
  async function onRemoveClick(event) {
    var link = event.target.closest && event.target.closest('a.remove-quan-cart');
    if (!link || !drawer() || !drawer().contains(link)) return;

    /* Let a modified click do the ordinary thing: it opens a new tab, and the
       current page — with its drawer — stays exactly as it is. */
    if (event.defaultPrevented || event.button !== 0) return;
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

    event.preventDefault();
    if (link.getAttribute('aria-busy') === 'true') return;   // guards a double click

    /* The row's own input carries the CURRENT key. Preferred over the href,
       which was written when this subtree last rendered and goes stale the
       moment any other line changes — the re-keying trap the reconcile engine
       is built around. The href is the fallback, not the source. */
    var row = link.closest('line-item, .line-item');
    var input = row && row.querySelector('.quantity-input[data-line-key]');
    var key = input && input.getAttribute('data-line-key');
    if (!key) {
      var m = /[?&]id=([^&]+)/.exec(link.getAttribute('href') || '');
      key = m && decodeURIComponent(m[1]);
    }
    if (!key) { window.location.assign(link.href); return; }  // nothing to work with: let it navigate

    link.setAttribute('aria-busy', 'true');
    try {
      /* Removing the protection line by hand is an opt-out, exactly as it is
         through the row's own link — without this the next reconcile pass
         re-adds the paid line the buyer just removed. Written BEFORE the
         request so a pass racing it cannot win. */
      var cart0 = window.wsCart && window.wsCart.lastCart;
      var line = ((cart0 && cart0.items) || []).filter(function (i) { return i.key === key; })[0];
      if (line && isProtectionLine(line)) setProtectionOptedOut(true);

      broadcastCartChange(await cartChange(key, 0), 'line-item:change');
    } catch (e) {
      /* The line is still there and the drawer is still open, which is a
         better failure than a navigation the buyer did not ask for. */
    } finally {
      link.removeAttribute('aria-busy');
    }
  }

  async function onProtectionToggleClick(event) {
    event.preventDefault();
    var link = event.currentTarget;
    if (link.getAttribute('aria-busy') === 'true') return; // guards a double click
    link.setAttribute('aria-busy', 'true');

    var row = link.closest('[data-ws-protection-add]');
    var isProtected = !!row && row.getAttribute('data-ws-protected') === '1';
    var p = (config && config.protection) || {};

    try {
      if (isProtected) {
        /* Opt-out is written BEFORE the removal, so even a reconcile racing this
           request cannot re-add what the buyer just took out. */
        setProtectionOptedOut(true);
        var cart = window.wsCart && window.wsCart.lastCart;
        var line = ((cart && cart.items) || []).filter(isProtectionLine)[0];
        if (line) broadcastCartChange(await cartChange(line.key, 0), 'line-item:change');
      } else {
        setProtectionOptedOut(false); // asking for it back clears the earlier opt-out
        await addVariantToCart(p.variantId, { _ws_protection: '1' });
      }
    } catch (e) {
      /* Spec 0.6: degrade silently. The label stays as it was. */
    } finally {
      link.removeAttribute('aria-busy');
    }
  }


  /* The target puts the price right-aligned on the same row as the stepper.
     Impact renders it inside .line-item-left-h, above, next to the variant. It
     is MOVED rather than duplicated, so the element stays Impact's and any
     behaviour attached to it survives — the same pattern used for the checkout
     submit and the close control.

     Idempotent via a data flag, because this runs again after every swap.

     The save chip is matched to its line via data-line-key on the quantity
     input (line-item.liquid:139), the same key Shopify's cart JSON exposes on
     each item, rather than by DOM order — Impact's loop skips shipping-tagged
     lines (cart-drawer.liquid:539), so index and array position can diverge.
     The gift label is matched the same way and for the same reason.

     Protection lines take a different branch entirely (applyProtectionCard):
     no gift label, no save chip, and the price is moved to the card's top
     level rather than into the stepper — see that function. */
  function restyleLineItems(innerEl, cart, moduleHost) {
    var items = (cart && cart.items) || [];
    /* Where the upsell and protection modules go. The drawer puts them inside
       the element it restyles; the cart page cannot, so it passes its own. */
    var mods = moduleHost || innerEl;

    innerEl.querySelectorAll('line-item.line-item').forEach(function (li) {
      /* renderProtectionPrompt (below) builds a controller-owned <line-item>
         that carries these same tag+class for CSS reuse (docs/ws-touchpoints
         #11), so this query re-matches it on every later pass. It has no
         data-line-key, so without this guard it would fall through to the
         normal branch below and have its already-correctly-placed pricing
         moved into the stepper row, corrupting the layout the very next time
         this function runs. Skip it entirely; renderProtectionPrompt owns
         its whole lifecycle. */
      if (li.hasAttribute('data-ws-protection-add')) return;

      var qty = li.querySelector('line-item-quantity');
      var pricing = li.querySelector('.line-item-pricing');

      var keyInput = li.querySelector('.quantity-input[data-line-key]');
      var key = keyInput && keyInput.getAttribute('data-line-key');
      var item = key && items.filter(function (i) { return i.key === key; })[0];

      if (item && isProtectionLine(item)) {
        applyProtectionCard(li, pricing);
        return;
      }

      if (qty && pricing && !qty.contains(pricing)) qty.appendChild(pricing);

      /* A zero line renders as "€0" where every other price on the card reads
         "€29,95". Impact hands the bare integer to the currency app, which
         formats what it is given and does not add the decimals back.
         Deliberately NARROW: only a price whose numeric part is exactly "0" is
         touched, and only when the cart agrees that line is free. Rewriting
         prices generally would undo the app's own conversion — this cannot,
         because zero converts to zero in every currency.
         Verified the app does not revert the node afterwards. */
      if (pricing && item && !item.final_line_price) {
        var priceNode = pricing.querySelector('.current-price');
        if (priceNode && /^[^\d]*0[^\d]*$/.test(priceNode.textContent.trim())) {
          var moneyNode = priceNode.querySelector('.trans-money') || priceNode;
          moneyNode.textContent = formatMoney(0);
        }
      }

      /* Impact's remove control is an empty <a>; its bin is a ::before carrying
         a CDN background-image at 17px. The reference draws a 19px stroked
         glyph (WELLX_Cart_Drawer.html:311), so the ::before is suppressed in
         CSS and the reference's own paths are injected here. Idempotent: this
         runs again after every Impact swap. */
      var rm = li.querySelector('a.remove-quan-cart');
      if (rm && !rm.querySelector('svg')) {
        rm.appendChild(trashIcon());
      }
      /* The only thing inside the anchor is an aria-hidden icon, so without
         this the control has no accessible name at all. Set every pass, not
         just when the icon is injected: Impact swaps this subtree wholesale and
         a re-used anchor can arrive already carrying an <svg> but no label. */
      if (rm) {
        rm.setAttribute('aria-label',
          (config.strings && config.strings.removeItem) || 'Remove item');
      }

      /* A gift is locked: no stepper, no trash (spec 3). It is not the
         customer's to edit — the engine owns it and removes it the moment the
         tier stops being met. A BOGO-zeroed line is NOT a gift and deliberately
         keeps both controls. Class, not inline styles, so it survives every
         re-render and lives with the rest of the design in CSS. */
      li.classList.toggle('ws-cart__line--gift', !!(item && isGiftLine(item)));

      applyGiftLabel(li, item);
      applyVariantLabel(li, item);

      /* The chip sits on its OWN row between the variant and the stepper row —
         not inside the price column. That is where the card design puts it, and
         it also stops a long discount name from squeezing the price. */
      var existingChip = li.querySelector('.ws-cart__save-chip');
      if (existingChip) existingChip.remove();

      var chip = item && buildSaveChip(item);
      if (chip) {
        var info = li.querySelector('.line-item__info');
        var qtyRow = li.querySelector('line-item-quantity');
        if (info && qtyRow) info.insertBefore(chip, qtyRow);
        else if (info) info.appendChild(chip);
      }
    });

    renderProtectionPrompt(mods, cart);
    renderUpsell(mods, cart);
  }

  // -------------------------------------------------------------------------
  // Upsell rail (WELLX_Cart_Drawer.html:349-389)
  // -------------------------------------------------------------------------

  /* Which products the rail may offer. Everything the merchant configured is
     already resolved server-side into config.upsells.candidates, EXCEPT the
     `recommendations` source, which has no Liquid API and is fetched once per
     page from Shopify's own engine. */
  var recState = { fetched: false, products: null };

  /* Where a module (the upsell rail, the protection row) should be appended.

     In the drawer that is .cart-drawer__line-items. On the cart page there is no
     such element — the line items live in a <tbody> — so the host is passed in
     explicitly and this falls back to it. Never assume the drawer's shape: that
     assumption used to be a hard querySelector(...).appendChild, which would
     throw on any other surface. */
  function moduleContainer(scope) {
    return scope.querySelector('.cart-drawer__line-items') || scope;
  }

  function upsellSeedProductId(cart) {
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (!isProtectionLine(items[i]) && !isGiftLine(items[i])) return items[i].product_id;
    }
    return null;
  }

  function fetchRecommendations(cart) {
    var up = config.upsells || {};
    if (up.source !== 'recommendations') return Promise.resolve(null);
    if (recState.fetched) return Promise.resolve(recState.products);
    var seed = upsellSeedProductId(cart);
    if (!seed) return Promise.resolve(null);
    recState.fetched = true;
    return fetch('/recommendations/products.js?product_id=' + seed + '&limit=8', {
      headers: { 'Accept': 'application/json' }
    })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (data) {
        var list = (data && data.products) || [];
        recState.products = list.map(function (p) {
          var v = (p.variants || []).filter(function (x) { return x.available; })[0] || (p.variants || [])[0];
          if (!v) return null;
          return {
            id: p.id,
            title: p.title,
            url: p.url,
            image: p.featured_image || (p.images && p.images[0]) || '',
            price: v.price,
            /* Only a genuine compare-at above the selling price. Never derived
               from anything else, per the no-invented-savings rule. */
            compareAt: (v.compare_at_price && v.compare_at_price > v.price) ? v.compare_at_price : 0,
            /* Shopify's recommendations payload carries no review metafields,
               so a recommendations-sourced card never shows stars. That is the
               honest outcome, not a gap to paper over. */
            rating: null,
            ratingCount: 0,
            /* Carried solely so the shipping-tag guard in upsellCards can see
               it; nothing renders from tags. */
            tags: p.tags || [],
            variants: (p.variants || []).filter(function (x) { return x.available; })
              .map(function (x) { return { id: x.id, title: x.title, price: x.price }; })
          };
        }).filter(Boolean);
        return recState.products;
      })
      .catch(function () { recState.products = []; return recState.products; });
  }

  /* A shipping-protection product must never be offered as an upsell: it is a
     fee, not merchandise, and an Add button beside it reads as a second charge.

     Tagged `shipping` is the signal Impact itself uses to keep this product out
     of its own line-item loop (sections/cart-drawer.liquid:539), so this reuses
     the theme's existing convention rather than inventing a parallel one.

     This is deliberately NOT the ws_protection_product setting. That setting is
     the merchant's and is empty on this store right now, which is exactly when
     the rail is unguarded — excludeVariantIds resolves to [0,0,0,0] and excludes
     nothing. A guard that only works once someone fills a field is not a guard.
     Both run: this one on the product, that one on the chosen variant ids. */
  function isFeeProduct(card) {
    var tags = card && card.tags;
    if (!tags || !tags.length) return false;
    for (var i = 0; i < tags.length; i++) {
      if (String(tags[i]).trim().toLowerCase() === 'shipping') return true;
    }
    return false;
  }

  function upsellCards(cart, pool) {
    var up = config.upsells || {};
    var excluded = up.excludeVariantIds || [];
    var inCart = {};
    ((cart && cart.items) || []).forEach(function (i) {
      inCart[i.product_id] = true;
    });

    return (pool || []).filter(function (c) {
      if (!c || !c.variants || !c.variants.length) return false;
      if (isFeeProduct(c)) return false;
      /* The gift and the protection fee are never offered back to the buyer. */
      for (var i = 0; i < c.variants.length; i++) {
        if (excluded.indexOf(c.variants[i].id) !== -1) return false;
      }
      if (up.hideInCart && inCart[c.id]) return false;
      return true;
    }).slice(0, up.max || 3);
  }

  /* Rating row renders ONLY from real review data. No data means no row —
     never placeholder stars (ws-touchpoints, "Star ratings render only from
     real reviews.rating and reviews.rating_count"). */
  function upsellRatingRow(card) {
    if (!(config.upsells || {}).showStars) return null;
    if (card.rating == null || !(card.ratingCount > 0)) return null;
    var p = document.createElement('p');
    p.className = 'ws-card__rating';
    /* SVG, not U+2605. The star was a text glyph, and Galano Grotesque does not
       contain that codepoint — so it silently fell to whatever system face did,
       putting a different typeface inside a line of Galano. An SVG cannot fall
       back. Decorative: the value and count beside it carry the meaning.

       Note the reference (.claude/Tasks/WELLX_Cart_Drawer.html:362) draws this as
       &#9733;, the same character, so there was no SVG star to copy — this path is
       new, sized and filled to land where the 12px glyph did. */
    var star = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    star.setAttribute('class', 'ws-star');
    star.setAttribute('viewBox', '0 0 24 24');
    star.setAttribute('fill', 'currentColor');
    star.setAttribute('aria-hidden', 'true');
    star.setAttribute('focusable', 'false');
    var point = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    point.setAttribute('d', 'M12 2.6l2.9 5.88 6.49.94-4.7 4.58 1.11 6.46L12 17.4l-5.8 3.06 1.11-6.46-4.7-4.58 6.49-.94z');
    star.appendChild(point);
    p.appendChild(star);
    /* One decimal, as the reference prints it — the metafield carries two
       ("4.94" renders there as "4.9"). Number() first: the value arrives as a
       string from the metafield. */
    var shown = Number(card.rating);
    p.appendChild(document.createTextNode(
      ' ' + (isFinite(shown) ? shown.toFixed(1) : card.rating) + ' '));
    var count = document.createElement('span');
    count.textContent = '(' + card.ratingCount + ')';
    p.appendChild(count);
    return p;
  }

  function buildUpsellCard(card, alreadyInCart) {
    var art = document.createElement('article');
    art.className = 'ws-card';
    art.setAttribute('data-ws-upsell-card', String(card.id));

    var media = document.createElement('div');
    media.className = 'ws-card__media';
    var img = document.createElement('img');
    img.className = 'ws-card__img';
    img.src = card.image || '';
    img.alt = '';
    img.loading = 'lazy';
    media.appendChild(img);

    /* Badge only from a REAL compare-at above the selling price, which Liquid
       already refuses to emit otherwise. */
    if (card.compareAt > card.price) {
      var pct = Math.round((card.compareAt - card.price) / card.compareAt * 100);
      if (pct > 0) {
        var badge = document.createElement('span');
        badge.className = 'ws-card__badge';
        badge.textContent = '-' + pct + '%';
        media.appendChild(badge);
      }
    }
    art.appendChild(media);

    var title = document.createElement('h4');
    title.className = 'ws-card__title';
    title.title = card.title;
    title.textContent = card.title;
    art.appendChild(title);

    var rating = upsellRatingRow(card);
    if (rating) art.appendChild(rating);

    /* TWO LINES, always in this order: struck compare-at above, selling price
       below. It used to be an <s> and a bare TEXT NODE side by side in one
       paragraph, which is why long money overflowed — two inline runs cannot
       wrap between themselves inside a 116px card, so "Rs.13,200.00 Rs.9,900.00"
       simply ran out of the card and over its neighbour.

       The selling price gets a real element rather than staying a text node so
       the two lines can be typed apart, 12/400 against 14/700. */
    var price = document.createElement('p');
    price.className = 'ws-card__price';
    if (card.compareAt > card.price) {
      var was = document.createElement('s');
      was.className = 'ws-card__price-was';
      was.textContent = formatMoney(card.compareAt);
      price.appendChild(was);
    }
    var now = document.createElement('span');
    now.className = 'ws-card__price-now';
    now.textContent = formatMoney(card.price);
    price.appendChild(now);
    art.appendChild(price);

    /* Size box then ADD, as the design draws them. The select is a real
       <select> — the platform's own picker, keyboard and screen-reader operable
       — and renders only when the product has more than one available variant,
       so a single-variant card goes straight from price to ADD. */
    var variants = card.variants;
    var chosen = variants[0];
    var strings = config.strings || {};

    var sel = null;
    if (variants.length > 1) {
      sel = document.createElement('select');
      sel.className = 'ws-card__select';
      sel.setAttribute('aria-label', card.title);
      variants.forEach(function (v) {
        var o = document.createElement('option');
        o.value = String(v.id);
        o.textContent = v.title;
        sel.appendChild(o);
      });
      sel.addEventListener('change', function () {
        var v = variants.filter(function (x) { return String(x.id) === sel.value; })[0];
        if (v) chosen = v;
      });
      art.appendChild(sel);
    }

    var add = document.createElement('button');
    add.type = 'button';
    add.className = 'ws-card__add';
    add.textContent = alreadyInCart
      ? (strings.upsellAdded || strings.upsellAdd || 'Add')
      : (strings.upsellAdd || 'Add');
    /* Deliberately still enabled when it reads ADDED: the label reports what is
       in the cart, it does not forbid a second one. */
    if (alreadyInCart) add.setAttribute('data-ws-added', '');
    /* The visible label is just "ADD" on every card, so the accessible name
       carries which product it adds. */
    add.setAttribute('aria-label', (strings.upsellAdd || 'Add') + ' \u2014 ' + card.title);

    add.addEventListener('click', function () {
      if (add.disabled) return;
      add.disabled = true;
      var restore = add.textContent;
      add.textContent = strings.upsellAdding || restore;
      addVariantToCart(chosen.id)
        .then(function () {
          add.textContent = strings.upsellAdded || restore;
          add.setAttribute('data-ws-added', '');
          /* Spoken once, here, rather than from the repaint — the repaint runs
             on every cart change and would re-announce cards nobody touched. */
          var region = document.getElementById('status-announcement');
          if (region) {
            region.textContent = (strings.upsellAdded || 'Added') + ' \u2014 ' + card.title;
          }
          /* The label set here only has to survive until the repaint, which
             rebuilds this card with the same state from the cart itself. */
        })
        .catch(function () {
          add.textContent = restore;
          add.disabled = false;
        });
    });

    art.appendChild(add);
    return art;
  }

  function renderUpsell(innerEl, cart) {
    var up = config.upsells || {};
    var existing = innerEl.querySelector('.ws-upsell');

    /* Empty states render nothing: no empty box, no spacer, no placeholder. */
    var dropPad = function () {
      var pad = innerEl.querySelector('.ws-cart__scrollpad');
      if (pad) pad.remove();
    };

    if (!up.enabled || !hasPaidLine(cart)) {
      if (existing) existing.remove();
      dropPad();
      return;
    }

    var paint = function (pool) {
      var cards = upsellCards(cart, pool);
      var host = innerEl.querySelector('.ws-upsell');
      if (!cards.length) {
        if (host) host.remove();
        dropPad();
        return;
      }

      var sec = document.createElement('section');
      sec.className = 'ws-upsell';
      sec.setAttribute('data-ws-upsell', '');

      var head = document.createElement('h3');
      head.className = 'ws-upsell__head';
      /* Text only. The box icon that used to sit here is gone by request — the
         heading is a label, not a module marker, and the rail's own cards
         already say what the section is. */
      head.appendChild(document.createTextNode(
        up.heading || (config.strings && config.strings.upsellHeading) || ''
      ));
      sec.appendChild(head);

      var rail = document.createElement('div');
      rail.className = 'ws-upsell__rail';
      /* Which of these is already in the cart. The ADDED label is derived from
         THIS, not from the click that caused it: the rail repaints on the cart
         change every add triggers, so a label set in the click handler was
         destroyed a moment later and the button came back reading "Add" — no
         feedback at all whenever the card stays (hideInCart off). Derived from
         cart state, it survives every repaint and clears itself when the line
         is removed, which is what the reference does. */
      var inCart = {};
      ((cart && cart.items) || []).forEach(function (i) { inCart[i.product_id] = true; });
      cards.forEach(function (c) { rail.appendChild(buildUpsellCard(c, !!inCart[c.id])); });
      sec.appendChild(rail);

      host = innerEl.querySelector('.ws-upsell');
      if (host) host.replaceWith(sec);
      else moduleContainer(innerEl).appendChild(sec);

      /* The rail is the last scrollable thing above a pinned footer, so without
         trailing space its ADD buttons sit flush against the footer edge at the
         scroll bottom and read as clipped. A real element rather than padding on
         the container: it belongs to the rail and leaves with it. */
      var pad = innerEl.querySelector('.ws-cart__scrollpad');
      if (!pad) {
        pad = document.createElement('div');
        pad.className = 'ws-cart__scrollpad';
        pad.setAttribute('aria-hidden', 'true');
      }
      sec.insertAdjacentElement('afterend', pad);
    };

    if (up.source === 'recommendations') {
      fetchRecommendations(cart).then(function (list) { paint(list || []); });
    } else {
      paint(up.candidates || []);
    }
  }

  /* Totals-derived, so this runs immediately on cart:change and matches
     Impact's synchronous footer replacement rather than waiting for the swap. */
  function updateCount(count) {
    var subline = document.querySelector('[data-ws-subline]');
    if (subline) subline.textContent = itemCountLabel(count);
    var badge = document.querySelector('[data-ws-badge]');
    if (badge) badge.textContent = count;
    stampCartCounts(count);
  }

  /* IMPACT'S <cart-count> HAS THREE RUNTIME PATHS TO THE BADGE, and every one
     of them assigns Shopify's raw item_count:

       theme.js:1213  cart:change   -> this.itemCount = detail.cart.item_count
       theme.js:1214  cart:refresh  -> _updateFromServer -> fetched item_count
       theme.js:1215  pageshow      -> _updateFromServer  (bfcache restore)

     Re-stamping after each would work but shows the raw figure first, and the
     brief is explicit that no frame may carry it. So the value is intercepted
     rather than corrected: an own `itemCount` property on the element wraps
     the prototype setter and translates raw into the visible count before
     Impact's own animation ever runs. Impact's badge logic is not forked — its
     setter still does the work, it is simply handed the right number.

     The element is claimed, never replaced, so the count-up animation, the
     opacity handling and the header's layout are all exactly Impact's. */
  function claimCartCount(el) {
    if (!el || el.__wsCountClaimed) return;
    var proto = Object.getPrototypeOf(el);
    var desc = proto && Object.getOwnPropertyDescriptor(proto, 'itemCount');
    /* Not upgraded yet — the custom element defines its accessors on upgrade.
       stampCartCounts writes textContent directly in that window and claims it
       on a later pass. */
    if (!desc || !desc.set) return;
    el.__wsCountClaimed = true;
    el.__wsNativeSet = desc.set;

    Object.defineProperty(el, 'itemCount', {
      configurable: true,
      get: function () { return desc.get.call(this); },
      set: function (raw) {
        if (this.__wsStamping) { desc.set.call(this, raw); return; }
        var last = window.wsCart && window.wsCart.lastCart;
        if (last && last.item_count === raw) {
          desc.set.call(this, wsVisibleCount(last));
          return;
        }
        /* Raw arrived from a cart this layer has not seen — cart:refresh, or a
           bfcache restore. Printing it would show the gift and the protection.
           Hold the figure on screen and correct it from the network instead;
           a stale-but-honest number beats a wrong one for the ~100ms it takes. */
        refreshCountFromServer();
      }
    });
  }

  var countRefreshInFlight = false;
  function refreshCountFromServer() {
    if (countRefreshInFlight) return;
    countRefreshInFlight = true;
    fetch(window.Shopify.routes.root + 'cart.js', { headers: { Accept: 'application/json' } })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        window.wsCart.lastCart = cart;
        updateCount(wsVisibleCount(cart));
      })
      .catch(function () { /* Spec 0.6: leave the last good figure alone. */ })
      .then(function () { countRefreshInFlight = false; });
  }

  function stampCartCounts(count) {
    var els = document.querySelectorAll('cart-count');
    for (var i = 0; i < els.length; i++) {
      var el = els[i];
      claimCartCount(el);

      /* A badge INSIDE .cart-drawer__inner is written synchronously, never
         through Impact's setter.

         Impact's setter animates first and assigns textContent only after the
         250ms transition resolves (theme.js:1240-1243). The drawer's badge sits
         inside the element Impact reassigns wholesale, and those swaps arrive in
         bursts — measured at 3399ms, 3400ms x5 and 4655ms after a single line
         removal. The deferred write therefore lands on a node that has already
         been detached and discarded, leaving the drawer's badge showing the
         pre-change number while the header's badge, outside the inner and never
         replaced, showed the new one. That was the header-says-1, bag-says-2
         split.

         The animation is worth nothing on a node that is recreated on every
         swap, so it is skipped there and the value written directly. */
      var volatile_ = !!(el.closest && el.closest('.cart-drawer__inner'));

      if (el.__wsNativeSet && !volatile_) {
        /* Through the native setter so the animation is Impact's, with the
           flag set so our own wrapper passes the derived number straight on. */
        el.__wsStamping = true;
        try { el.__wsNativeSet.call(el, count); } catch (e) {}
        el.__wsStamping = false;
      } else if (parseInt(el.textContent, 10) !== count) {
        el.textContent = count;
      }
    }

    /* The accessible name lives on the link, not in the bubble: Impact's setter
       assigns textContent, which would delete an sr-only span on first update.
       The bubble is aria-hidden in the markup, so this is the only announcement
       and it carries the same derived number. */
    var link = document.querySelector('[data-ws-cart-link]');
    if (link) {
      /* The translated word comes from the link itself, stamped there by
         Liquid — this layer has no business re-translating what the theme has
         already localised into 28 locales. */
      var cartWord = link.getAttribute('data-ws-cart-label') || 'Cart';
      /* itemCountShort ("2 items"), not the drawer's subline form ("2 items in
         cart") — it has to match what Liquid stamped at first paint, and
         "Cart, 2 items in cart" reads badly read aloud. */
      link.setAttribute('aria-label', cartWord + ', ' + itemCountLabel(count, 'itemCountShort'));
    }
  }

  /* Also totals-derived, and the brief's commercial argument applies hardest
     here: the bar exists to say "add twelve euros more and you unlock this".
     A bar that waits 1250ms for the swap has already lost the moment it is
     there to catch. Rebuilt in place rather than patched, because node count
     itself changes when a gift goes out of stock. */
  function renderRewards(cart) {
    var innerEl = inner();
    if (!innerEl) return;

    var built = buildRewardsBar(cart);
    var existing = innerEl.querySelector('.ws-rewards');

    if (!built) {
      if (existing) existing.remove(); // zero tiers means zero markup
      return;
    }

    if (existing) {
      existing.replaceWith(built);
    } else {
      var header = innerEl.querySelector('.ws-cart__header');
      if (header) header.insertAdjacentElement('afterend', built);
      else innerEl.insertBefore(built, innerEl.firstChild);
    }

    announceUnlock(cart);
  }

  /* BUY 1 GET 1 FREE NOTE — a fixed explanatory line, nothing more.
     Spec: WELLX BOGO Note Spec, option 3, chosen by Floris 29 Oct 2026.

     Shopify's automatic Buy X Get Y discount never adds the Y item itself, so a
     shopper can reach checkout with one pair believing they are getting two.
     This says so in the bag. It has no button, no states and no discount
     detection: it reads the cart and decides only whether to exist.

     DELIBERATELY NOT a live region. The text is identical every time it
     appears, so announcing it would interrupt a screen reader with a sentence
     that has not changed since the last cart action. It is ordinary prose in
     reading order, which is where a shopper meets it anyway.

     Returns nothing when the merchant has not switched it on: with the feature
     off, Liquid emits no "bogo" object at all, so this is one property lookup
     and an early return on every cart change. */
  function bogoShouldShow(cart) {
    var b = (config && config.bogo) || null;
    if (!b) return false;

    var items = (cart && cart.items) || [];
    /* isCountable is the rule of record for "a line the shopper manages":
       not a tier gift, not the protection fee, not a subscription. Reusing it
       means the note can never disagree with the rewards maths about what
       counts as a real product. */
    var countable = items.filter(isCountable);
    if (!countable.length) return false;           // empty bag, or extras only

    if (b.showWhen === 'always') return true;

    /* deal_items. An empty allow-list means the merchant picked no
       collections, which is "show nothing" — never "match everything". A
       permissive empty list would put the note on every cart in the store. */
    var ids = b.productIds || [];
    if (!ids.length) return false;

    var inDeal = {};
    for (var i = 0; i < ids.length; i++) inDeal[String(ids[i])] = true;

    for (var j = 0; j < countable.length; j++) {
      if (inDeal[String(countable[j].product_id)]) return true;
    }
    return false;
  }

  function buildBogoNote() {
    var b = (config && config.bogo) || {};
    var strings = (config && config.strings) || {};
    var title = b.title || strings.bogoTitle || '';
    var text = b.text || strings.bogoText || '';
    /* Both blank means the merchant cleared the copy AND the locale carries
       none. An empty card is worse than no card. */
    if (!title && !text) return null;

    var note = document.createElement('div');
    note.className = 'ws-bogo-note';
    note.setAttribute('data-ws-bogo', '');

    var icon = document.createElement('span');
    icon.className = 'ws-bogo-note__icon';
    icon.setAttribute('aria-hidden', 'true');
    /* The spec's info glyph. Same construction as trashIcon(): the SVG is the
       only thing built from a string here, and it carries no merchant text. */
    icon.innerHTML = '<svg viewBox="0 0 24 24" focusable="false">' +
      '<circle cx="12" cy="12" r="9"></circle>' +
      '<path d="M12 11v6M12 7.5v.1"></path></svg>';

    var body = document.createElement('div');
    body.className = 'ws-bogo-note__body';

    if (title) {
      var h = document.createElement('p');
      h.className = 'ws-bogo-note__title';
      h.textContent = title;          // textContent, never innerHTML: merchant copy
      body.appendChild(h);
    }
    if (text) {
      var p = document.createElement('p');
      p.className = 'ws-bogo-note__text';
      p.textContent = text;
      body.appendChild(p);
    }

    note.appendChild(icon);
    note.appendChild(body);
    return note;
  }

  /* scope is the element the note lives in, and anchor the element it follows.
     The drawer and the cart page differ on both, so they are passed in rather
     than queried here: the cart page has no .ws-cart__header, and its rewards
     bar is inserted before the table by applyCartPage rather than by
     renderRewards. */
  function renderBogoNote(cart, scope, anchor) {
    if (!scope) return;
    var existing = scope.querySelector('.ws-bogo-note');

    if (!bogoShouldShow(cart)) {
      if (existing) existing.remove();
      return;
    }

    var built = buildBogoNote();
    if (!built) {
      if (existing) existing.remove();
      return;
    }

    if (existing) {
      existing.replaceWith(built);    // replaced in place, so nothing below moves
      return;
    }

    /* A null anchor is a decision, not a missing value: the caller is saying
       "first child of scope". Only undefined asks this function to look. */
    var after = anchor === null ? null
      : (anchor || scope.querySelector('.ws-rewards') || scope.querySelector('.ws-cart__header'));
    if (after) after.insertAdjacentElement('afterend', built);
    else scope.insertBefore(built, scope.firstChild);
  }

  /* THE DRAWER'S CALL, and the note goes INSIDE the line-items container
     rather than beside the rewards bar.

     Reading order is identical either way. The container is not the bar's
     sibling — it sits inside Impact's .v-stack, which is itself the bar's
     sibling — but it is the next content after it, so the note still falls
     between the bar and the first product. Measured at 1440: bar ends y=178,
     note starts y=190, first line item y=285. What differs is the gutter. The bar is full-bleed with its own
     internal padding, while line items are inset by the container's padding
     (measured 14px at 1440, 12px at 390). A note placed beside the bar drew its
     dashed border hard against both drawer edges while every card below it sat
     inset, which is not the card the spec draws.

     Putting it in the container means it inherits that inset from the same
     declaration the line items do, so the left edges line up at every
     breakpoint and keep lining up if the padding is ever changed. It is also
     where the other controller-built modules already live: moduleContainer is
     what renderUpsell and renderProtectionPrompt are given. */
  function renderBogoNoteInDrawer(cart) {
    var innerEl = inner();
    if (!innerEl) return;
    var scope = moduleContainer(innerEl);
    /* anchor null = insert as the container's first child, above the first
       product. When moduleContainer falls back to innerEl (no container in the
       DOM yet) the rewards bar is the right anchor instead. */
    var anchor = scope === innerEl
      ? (innerEl.querySelector('.ws-rewards') || innerEl.querySelector('.ws-cart__header'))
      : null;
    renderBogoNote(cart, scope, anchor);
  }

  // -------------------------------------------------------------------------
  // Money (spec 0.7: presentment currency via the shop's own money format)
  // -------------------------------------------------------------------------

  /* Formatting is delegated to Intl with the PRESENTMENT currency code, not
     hand-rolled against shop.money_format.

     The earlier version did the latter and it was wrong in a way invisible in
     euros: shop.money_format is the SHOP's format and never changes with the
     market, so on a JPY market it rendered 1,000 yen as "€10.00" — wrong
     symbol and wrong decimal count. Intl knows that JPY takes no decimals and
     KWD takes three, and it knows each symbol, so the currency code is the only
     input needed.

     The divisor is 100 and that is Shopify's own normalisation, not an
     assumption about decimal places. Per the cart object docs: "For currencies
     without subunits, such as JPY and KRW, tenths and hundredths of a unit are
     appended. For example, 1000 Japanese yen is output as 100000." So the
     magnitude is consistent across zero- and two-decimal currencies.

     NOT VERIFIED: three-decimal currencies (KWD, BHD, JOD). The docs describe
     the zero-subunit case explicitly and say nothing about these. This store is
     EUR into NL so there is no live exposure, but do not assume it is proven —
     check before enabling a market with one. */
  var moneyFormatter;

  /* The SHOP's own money_format is authoritative, not Intl.
     This store runs "EUR {{amount_with_comma_separator}}", i.e. 29,95 — while
     Intl with the storefront locale ("en") renders 29.95. Both were on screen at
     once: Impact prints line prices through the shop format, and every amount
     this controller drew (subtotal, total, the rewards message, the save chip)
     came out with the other separator. Same card, two conventions.

     Shopify's tokens, all six of them, since a merchant can switch format in
     admin at any time and the drawer must follow without a code change. Intl
     stays as the fallback for a blank or tokenless format. */
  function formatMoney(subunits) {
    var cents = Math.max(0, Math.round(subunits));
    var fmt = (config.money && config.money.format) || '';

    function build(decimals, thousandsSep, decimalSep) {
      var fixed = (cents / 100).toFixed(decimals);
      var parts = fixed.split('.');
      var whole = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
      return decimals > 0 ? whole + decimalSep + parts[1] : whole;
    }

    if (fmt.indexOf('{{') !== -1) {
      return fmt.replace(/\{\{\s*(\w+)\s*\}\}/g, function (_, token) {
        switch (token) {
          case 'amount_no_decimals':                     return build(0, ',', '.');
          case 'amount_with_comma_separator':            return build(2, '.', ',');
          case 'amount_no_decimals_with_comma_separator':return build(0, '.', ',');
          case 'amount_with_apostrophe_separator':       return build(2, "'", '.');
          case 'amount_with_space_separator':            return build(2, ' ', ',');
          case 'amount_no_decimals_with_space_separator':return build(0, ' ', ',');
          default:                                       return build(2, ',', '.');
        }
      }).trim();
    }

    var major = cents / 100;
    if (moneyFormatter === undefined) {
      try {
        moneyFormatter = new Intl.NumberFormat(config.locale || 'en', {
          style: 'currency',
          currency: (config.money && config.money.currency) || 'EUR'
        });
      } catch (e) {
        moneyFormatter = null; // unknown code or no Intl; fall through below
      }
    }
    return moneyFormatter ? moneyFormatter.format(major) : major.toFixed(2);
  }

  // -------------------------------------------------------------------------
  // Rewards bar (spec 4)
  // -------------------------------------------------------------------------

  /* Spec 4: eligible spend and count EXCLUDE gift lines, the protection line
     and subscription lines, otherwise a gift or protection line inflates the
     very threshold that gates it.

     The money basis reuses Impact's own free-shipping-bar arithmetic, which is
     discount-aware: sum final_line_price, then subtract cart-level discount
     allocations. A naive subtotal unlocks tiers early on any discounted cart. */
  /* Both of these match on PRODUCT ID as well as our marker, because the
     realistic way an unmarked line appears is not forgery. The gift and the
     protection fee are real published products with real URLs — they have to
     be published to be addable at all — so anything can put an unmarked one in
     the cart by ordinary means. Marker-only matching leaves that open. */

  function protectionProductId() {
    return (config && config.protection && config.protection.productId) || 0;
  }

  function giftProductIds() {
    return ((config && config.tiers) || [])
      .map(function (t) { return t.giftProductId; })
      .filter(Boolean);
  }

  function isProtectionLine(item) {
    var props = item.properties || {};
    if (props._ws_protection) return true;
    var pid = protectionProductId();
    return pid !== 0 && item.product_id === pid;
  }

  /* Task 3 gating. Blank country list = every country, the same reading the
     section already applies to defaultOn's own guard (ws-cart-drawer.liquid,
     "COMPOSED-DEFAULT GUARD" comment) — so display and that guard can never
     disagree about what "blank" means. config.money.country is the buyer's
     CURRENT localization, resolved server-side at render time; it cannot go
     stale mid-session because changing country is a full page load (see
     scheduleLoadPass's own reasoning below), never an in-page transition. */
  function protectionCountryAllowed() {
    var countries = (config && config.protection && config.protection.countries) || [];
    if (!countries.length) return true;
    var current = (config.money && config.money.country) || '';
    return countries.indexOf(current) !== -1;
  }

  /* The row's whole existence gate: the merchant switch, a real product
     actually assigned (variantId/productId both resolve to 0 from Liquid
     when none is picked — the empty state the brief calls for), and the
     country allow-list. All three, every time; nothing here caches a
     once-true result past a config or market change. */
  function protectionGateOpen() {
    var p = (config && config.protection) || {};
    return !!p.enabled && protectionProductId() !== 0 && !!p.variantId && protectionCountryAllowed();
  }

  /* "Blank = localized default" — ws_protection_title's own info string.
     Never the raw product title: line-item.liquid renders that through
     .title-cart-drawer by default, and this replaces it deliberately so the
     card always reads as protection copy, not whatever the fee PRODUCT
     happens to be named in the catalogue. */
  function protectionTitleText() {
    var custom = config && config.protection && config.protection.title;
    return custom || (config.strings && config.strings.protectionTitle) || '';
  }

  /* The icon tile's stacked caption — two locale keys, not one string split
     on a delimiter. Safer for translators (no escape sequence to preserve
     correctly across 28 files) and it sidesteps relying on the browser to
     wrap plain text onto exactly two lines inside the 64px tile: that tile is
     a column flex item under align-items:center, which sizes a text child to
     its own content rather than stretching it to the container's cross-axis
     width, so an unbroken run is not guaranteed to wrap where intended.
     WELLX_Cart_Drawer.html:339 hardcodes the same two-line break with a
     literal <br> rather than leaving it to wrap, for the same reason. */
  function buildProtectionBadge() {
    var badge = document.createElement('span');
    badge.className = 'ws-protect__badge';
    var line1 = (config.strings && config.strings.protectionBadgeLine1) || '';
    var line2 = (config.strings && config.strings.protectionBadgeLine2) || '';
    badge.appendChild(document.createTextNode(line1));
    if (line1 && line2) badge.appendChild(document.createElement('br'));
    badge.appendChild(document.createTextNode(line2));
    return badge;
  }

  /* The gift needs one more condition than protection, or it breaks a real
     sale. Spec 9 sets the gate: judge a line on whether its real price is
     actually zero, not on whether it carries our marker. Someone who genuinely
     buys the gift item at full price is a customer, and their money must count
     toward the thresholds like anyone else's.

     Protection needs no such condition, because spec 5's "never alone" rule
     means a standalone protection purchase is never legitimate. */
  /* Gift identity is the MARKER, never the price. Keying it on
     final_line_price === 0 would sweep in every BOGO-zeroed line, and those are
     purchases: locking them would take the stepper and the delete button away
     from a customer editing an item they actually bought. The validator has its
     own, wider identity test (giftIdentity) for finding lines that should not
     be there; this one answers "is this line OURS". */
  function isGiftLine(item) {
    return !!(item && (item.properties || {})._ws_gift);
  }

  function isCountable(item) {
    if (isGiftLine(item) || isProtectionLine(item)) return false;
    if (item.selling_plan_allocation) return false; // subscription line
    return true;
  }

  function eligible(cart, basis) {
    if (!cart || !cart.items) return 0;
    var countable = cart.items.filter(isCountable);

    if (basis === 'item_count') {
      return countable.reduce(function (n, item) { return n + item.quantity; }, 0);
    }

    var gross = countable.reduce(function (sum, item) {
      return sum + (item.final_line_price || 0);
    }, 0);

    var cartDiscounts = (cart.cart_level_discount_applications || []).reduce(
      function (sum, d) { return sum + (d.total_allocated_amount || 0); }, 0
    );

    return Math.max(0, gross - cartDiscounts);
  }

  /* Spec 4: a gift needs all three — at least one paid, non-gift,
     non-protection, non-subscription line, because a gift can never exist
     alone; the gift variant available; and eligible spend past the threshold. */
  function hasPaidLine(cart) {
    if (!cart || !cart.items) return false;
    return cart.items.some(function (item) {
      return isCountable(item) && (item.final_line_price || 0) > 0;
    });
  }

  function evaluateTiers(cart) {
    var tiers = ((config && config.tiers) || []).slice();

    /* Spec 4: sort by resolved threshold at evaluation time, regardless of the
       order the merchant happens to have the tiers in. */
    tiers.sort(function (a, b) { return a.threshold - b.threshold; });

    var paid = hasPaidLine(cart);

    tiers.forEach(function (tier) {
      var have = eligible(cart, tier.basis);
      tier.have = have;
      tier.reached = have >= tier.threshold;
      if (tier.type === 'gift') {
        tier.reached = tier.reached && paid && tier.giftAvailable;
      }
    });

    return tiers;
  }

  function lockedMessage(tier) {
    var remaining = Math.max(0, tier.threshold - tier.have);
    var locked = tier.locked || {};

    /* The merchant override is a raw string with no plural forms, so it is
       substituted verbatim and never pluralised. Documented decision: an
       override is the merchant taking the wheel, and on an item-count basis
       that means their wording is used for every count in every language. The
       localized default below stays plural-correct. */
    if (locked.override) {
      return locked.override.replace('[amount]', displayRemaining(tier, remaining));
    }

    var forms = locked.forms || {};
    var template = forms.other;

    if (tier.basis === 'item_count') {
      var category = 'other';
      try {
        category = new Intl.PluralRules(config.locale || 'en').select(remaining);
      } catch (e) { /* fall through to other */ }
      template = forms[category] || forms.other;
    }

    if (!template) return '';

    return template
      .replace(/\{\{\s*amount\s*\}\}/g, displayRemaining(tier, remaining))
      .replace(/\[count\]/g, remaining)
      .replace('[amount]', displayRemaining(tier, remaining));
  }

  /* Spec 4: copy matches the basis. Money renders as formatted currency, items
     render as a count — never a bare number that reads as broken money. */
  function displayRemaining(tier, remaining) {
    return tier.basis === 'item_count' ? String(remaining) : formatMoney(remaining);
  }

  /* Node icons, verbatim from WELLX_Cart_Drawer.html:276 (truck) and :280
     (gift). Stroke, fill and width come from `.ws-rewards__node svg` in the
     stylesheet, so the markup carries only the viewBox and the shapes. The
     truck's two wheels and the gift's bow are <circle>/curved <path> elements
     that cannot be flattened into svgIcon()'s single d string. */
  var REWARD_ICON = {
    free_shipping:
      '<path d="M1 3h15v13H1z"/><path d="M16 8h4l3 3v5h-7z"/>' +
      '<circle cx="5.5" cy="18.5" r="2.5"/><circle cx="18.5" cy="18.5" r="2.5"/>',
    gift:
      '<path d="M20 12v10H4V12"/><path d="M2 7h20v5H2z"/><path d="M12 22V7"/>' +
      '<path d="M12 7H7.5a2.5 2.5 0 0 1 0-5C11 2 12 7 12 7z"/>' +
      '<path d="M12 7h4.5a2.5 2.5 0 0 0 0-5C13 2 12 7 12 7z"/>'
  };

  function rewardIconSvg(type) {
    return '<svg viewBox="0 0 24 24">' +
      (REWARD_ICON[type] || REWARD_ICON.gift) + '</svg>';
  }

  function tierIcon(type) {
    return svgIcon(type === 'free_shipping' ? ICON.truck : ICON.gift, '15', '1.6');
  }

  /* Spec 4: renders only when at least one valid tier is enabled. Zero tiers
     means zero markup, not an empty container. Tiers are already filtered in
     Liquid for enabled state, positive threshold and the always-free-shipping
     interaction, so an empty array here is the whole answer. */
  function buildRewardsBar(cart) {
    var tiers = evaluateTiers(cart);
    if (!tiers.length) return null;

    var n = tiers.length;
    var segW = 100 / n;

    /* The next tier to work toward is the first UNREACHED one — not
       tiers[metCount]. Tiers are sorted by raw threshold, but thresholds on
       different bases are not comparable (25 items vs 5000 cents), so a met
       tier can sit after an unmet one. Indexing by "how many are met" then
       pointed at a tier already reached, and the bar printed "Spend 0,00 more"
       on a reward the buyer had already unlocked. */
    var unmet = tiers.filter(function (t) { return !t.reached; });
    var allMet = unmet.length === 0;
    var next = unmet[0];
    var nextIndex = next ? tiers.indexOf(next) : -1;

    /* Markup is WELLX_Cart_Drawer.html:271-290, element for element. */
    var wrap = document.createElement('section');
    wrap.className = 'ws-rewards';
    wrap.setAttribute('aria-label', (config.strings && config.strings.a11yProgress) || 'Cart rewards progress');

    /* Nodes sit ABSOLUTELY inside the track at left:(i+1)/n x 100% — the
       reference puts the first of two at 50% and the second at 100%. The track
       carries 17px side margins (= node radius) so a node centred at 100% still
       renders fully inside the drawer. */
    var html = '<div class="ws-rewards__track">';
    html += '<div class="ws-rewards__fill" data-ws-fill></div>';
    tiers.forEach(function (tier, i) {
      /* Three states: reached, the one being worked toward, and locked. */
      var state = tier.reached ? ' ws-rewards__node--on'
        : (i === nextIndex ? ' ws-rewards__node--next' : '');
      html += '<span class="ws-rewards__node' + state + '"' +
        ' data-ws-node="' + escapeAttr(tier.id || String(i)) + '"' +
        ' style="left:' + ((i + 1) / n) * 100 + '%">' +
        rewardIconSvg(tier.type) + '</span>';
    });
    html += '</div>';

    html += '<div class="ws-rewards__labels">';
    tiers.forEach(function (tier, i) {
      var pos = ((i + 1) / n) * 100;
      /* --first / --last pull an edge label inward so it cannot clip; anything
         between them keeps the base rule's translateX(-50%) centring. The
         reference's own two-tier example shows exactly this: the 50% label
         plain and centred, the 100% label carrying --last. */
      var edge = pos >= 85 ? ' ws-rewards__label--last'
        : pos <= 15 ? ' ws-rewards__label--first' : '';
      html += '<span class="ws-rewards__label' + edge + (tier.reached ? ' is-on' : '') + '"' +
        ' data-ws-node-label="' + escapeAttr(tier.id || String(i)) + '"' +
        ' style="left:' + pos + '%">' + escapeHtml(tier.label || '') + '</span>';
    });
    html += '</div>';
    html += '<p class="ws-rewards__msg" data-ws-bar-status></p>';
    wrap.innerHTML = html;

    /* Fill advances only within the CURRENT segment. The previous threshold is
       only a valid floor when the two tiers share a basis — otherwise it would
       subtract an item count from an amount in cents. */
    var fillPct = 0;
    for (var i = 0; i < n; i++) {
      var t = tiers[i];
      if (t.reached) { fillPct += segW; continue; }
      var prev = (i > 0 && tiers[i - 1].basis === t.basis) ? tiers[i - 1].threshold : 0;
      var span = t.threshold - prev;
      var frac = span > 0 ? (t.have - prev) / span : 0;
      fillPct += segW * Math.min(1, Math.max(0, frac));
      break;
    }
    if (allMet) fillPct = 100;
    wrap.querySelector('[data-ws-fill]').style.width = Math.min(100, fillPct) + '%';

    var status = wrap.querySelector('[data-ws-bar-status]');
    if (allMet) {
      status.textContent = (config.strings && config.strings.allUnlocked) || '';
    } else {
      var msg = lockedMessage(next);
      var amount = displayRemaining(next, Math.max(0, next.threshold - next.have));
      var at = msg.indexOf(amount);
      if (at === -1) {
        status.textContent = msg;
      } else {
        status.appendChild(document.createTextNode(msg.slice(0, at)));
        /* <b>, per `.ws-rewards__msg b` in the reference's stylesheet. */
        var b = document.createElement('b');
        b.textContent = amount;
        status.appendChild(b);
        status.appendChild(document.createTextNode(msg.slice(at + amount.length)));
      }
    }

    return wrap;
  }

  /* Spec 4: polite screen-reader announcement on unlock. Announced only when
     the met count actually rises, so a re-render never re-announces. */
  var lastMetCount = null;

  function announceUnlock(cart) {
    var met = evaluateTiers(cart).filter(function (t) { return t.reached; }).length;
    if (lastMetCount !== null && met > lastMetCount) {
      var region = document.getElementById('status-announcement');
      if (region) region.textContent = (config.strings && config.strings.allUnlocked) || '';
    }
    lastMetCount = met;
  }

  // -------------------------------------------------------------------------
  // Totals, checkout, payment row (spec 6)
  // -------------------------------------------------------------------------

  function row(labelText, valueText, modifier) {
    var el = document.createElement('div');
    el.className = 'ws-cart__row' + (modifier ? ' ' + modifier : '');

    var label = document.createElement('span');
    label.className = 'ws-cart__row-label';
    label.textContent = labelText;

    var value = document.createElement('span');
    value.className = 'ws-cart__row-value';
    value.textContent = valueText;

    el.appendChild(label);
    el.appendChild(value);
    return el;
  }

  /* One row of the tint band: a 16px icon, its label, and a value on the right.
     The band is a filled chip, so its rows are lighter than the footer's own —
     they are supporting detail, not conclusions. */
  function bandRow(iconPath, labelText, valueText, valueModifier) {
    var el = document.createElement('div');
    el.className = 'ws-cart__band-row';

    var left = document.createElement('span');
    left.className = 'ws-cart__shipping-band-label';
    if (iconPath) left.appendChild(svgIcon(iconPath, '16', '1.5'));
    left.appendChild(document.createTextNode(labelText || ''));

    var right = document.createElement('span');
    right.className = 'ws-cart__shipping-band-value' + (valueModifier ? ' ' + valueModifier : '');
    right.textContent = valueText || '';

    el.appendChild(left);
    el.appendChild(right);
    return el;
  }

  function buildTotals(cart) {
    var st = (config && config.strings) || {};
    var stack = document.createElement('div');
    stack.className = 'ws-cart__totals';

    /* THE SUBTOTAL CONTRACT: the figure shown is the PRE-DISCOUNT total, so
       what the buyer reads down the column actually resolves —
       Subtotal − Item Discounts = Total. Every one of the three is Shopify's
       own number, and they are consistent by construction:
       original_total_price − total_discount === total_price.

       The previous build showed items_subtotal_price, which is already NET of
       line-level discounts. On a cart with a live BOGO that printed
       "Subtotal €32,45 / Total €32,45" with the €29,95 saving nowhere on
       screen — the buyer could not see the discount they had earned, and the
       arithmetic looked like nothing had happened. */
    var count = wsVisibleCount(cart);
    var subLabel = st.subtotal + ' (' + itemCountLabel(count, 'itemCountShort') + '):';
    stack.appendChild(row(subLabel, formatMoney(cart.original_total_price || 0)));

    /* "Includes a free gift" shows whenever a gift line is actually in the cart.
       It used to be gated on ws_gift_value_line as well, which was a misreading
       of that setting: its own label is "Show gift 'WORTH' VALUE in the
       free-gift line" and its info is entirely about the compare-at figure and
       who is liable for it. So the setting gates the figure, not the sentence —
       which is also what the reference does, where the line appears with no
       amount attached.

       That distinction matters beyond tidiness. The sentence is a statement of
       fact about the cart; the "(worth X)" is a price claim the merchant is
       answerable for. Only the second one needs an opt-in, and it still has it.

       A gift is a value statement, not a savings claim, so the line stays
       visually and logically apart from the discount row BELOW it. It sits
       directly under the subtotal, which is where the reference puts it —
       previously it came after the band, which read as a footnote to the
       discount rather than a statement about the subtotal. */
    var giftLine = ((cart && cart.items) || []).filter(isGiftLine)[0];
    if (giftLine) {
      var worth = config.footer && config.footer.giftValueLine
        ? giftCompareAtFor(giftLine) : 0;
      var text = st.includesFreeGift || '';
      if (worth > 0 && st.giftWorth) {
        text += ' (' + st.giftWorth.split('[amount]').join(formatMoney(worth)) + ')';
      }
      var giftLineEl = document.createElement('p');
      giftLineEl.className = 'ws-cart__gift-value';
      giftLineEl.textContent = text;
      stack.appendChild(giftLineEl);
    }


    /* THE BAND. A filled chip carrying only rows that have something TRUE to
       say. It is built first and appended only if a row survived, so it can
       never render as an empty tinted box. */
    var band = document.createElement('div');
    band.className = 'ws-cart__shipping-band';

    /* Item discounts: real allocations Shopify has already applied, never
       compare-at arithmetic. total_discount is the sum of both halves (line and
       cart level), which is exactly right HERE because the subtotal above is
       the pre-discount figure — the trap it used to be was subtracting it from
       a subtotal that already had the line half inside it. */
    var discount = cart.total_discount || 0;
    if (discount > 0) {
      band.appendChild(bandRow(ICON.tag, st.itemDiscounts || '', '-' + formatMoney(discount)));
    }

    /* Shipping has TWO states and no hedge. Either it is genuinely free and the
       band says so, or the row is absent. "Calculated at checkout" as a totals
       row was a third state that promised nothing and only added noise; the tax
       note below already carries that information. */
    var shippingFree = (config.footer && config.footer.shippingBand) ||
      evaluateTiers(cart).some(function (t) { return t.type === 'free_shipping' && t.reached; });

    if (shippingFree) {
      var shipRow = bandRow(ICON.truck, st.shippingLabel || '',
        st.shippingFree || '', 'ws-cart__shipping-band-value--free');
      /* Only the shipping row uppercases — the discount row keeps its sentence
         case, which is what the reference draws. */
      shipRow.classList.add('ws-cart__band-row--shipping');
      band.appendChild(shipRow);
    }

    if (band.children.length) stack.appendChild(band);

    /* Its own row, both halves heavy, and no currency suffix — so money, not
       money_with_currency. */
    stack.appendChild(row(st.total + ':', formatMoney(cart.total_price || 0),
      'ws-cart__row--total'));

    /* The caption is dropped from the compact footer, with ONE exception.

       When shipping is genuinely free the band already says so and the caption
       is pure redundancy — that is the state in the reference and the state of
       this store. When shipping is NOT free, removing it leaves a bare "Total"
       that a buyer can reasonably read as the final amount when a delivery
       charge is still to come. Checkout carries the binding disclosure either
       way (CRD Art. 6 attaches before the consumer is bound, not in a cart
       preview), so this is a fairness call rather than a legal one — but a
       total that is not the total is worth one line.

       Say the word and it goes unconditionally. */
    if (!shippingFree) {
      var note = document.createElement('p');
      note.className = 'ws-cart__tax-note';
      note.textContent = (config.footer && config.footer.taxesIncluded)
        ? (st.taxNoteIncluded || '') : (st.taxNoteExcluded || '');
      stack.appendChild(note);
    }

    return stack;
  }

  /* The gift's "worth" comes from the configured tier's compare-at, emitted by
     Liquid — never computed from anything on the cart line, which is already
     zero. Returns 0 when there is no honest figure to show. */
  function giftCompareAtFor(line) {
    var marker = (line && line.properties && line.properties._ws_gift) || '';
    var tier = ((config && config.tiers) || []).filter(function (t) {
      return t.id === marker;
    })[0];
    return (tier && tier.giftCompareAt) || 0;
  }


  /* Renders only when the merchant has typed one. Never a default. */
  function buildTrustLine() {
    var text = config.footer && config.footer.trustLine;
    if (!text) return null;
    var p = document.createElement('p');
    p.className = 'ws-cart__trust';
    p.textContent = text;
    return p;
  }

  function inlineIcon(d, size, marker) {
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 20 20');
    svg.setAttribute('width', size);
    svg.setAttribute('height', size);
    svg.setAttribute('aria-hidden', 'true');
    svg.setAttribute('fill', 'none');
    svg.setAttribute('stroke', 'currentColor');
    svg.setAttribute('stroke-width', '1.6');
    svg.setAttribute(marker, '');

    var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', d);
    svg.appendChild(path);
    return svg;
  }

  function skinCheckout(footer) {
    var native = footer.querySelector('button[name="checkout"], input[name="checkout"]');
    if (!native) return null;

    native.classList.add('ws-cart__checkout');
    native.setAttribute('data-ws-radius', (config.footer && config.footer.checkoutRadius) || 'pill');

    /* Spec 6: lock icon + label + arrow, wrapped around a label WE own.

       Impact's own text is "Secured checkout". The reference reads CHECKOUT, so
       the label now comes from ws_cart.totals.checkout like every other string
       in this drawer rather than from the theme — otherwise the wording is
       untranslatable and unchangeable from our side. The native element, its
       name="checkout" and its submit behaviour are untouched; only the text
       node is replaced. Guarded on the marker so a re-render never stacks
       duplicates. */
    if (native.tagName === 'BUTTON' && !native.querySelector('[data-ws-lock]')) {
      var label = (config.strings && config.strings.checkout) || '';
      if (label) {
        /* Impact keeps its label in a bare <div>, not a text node, so rewrite
           THAT element's text rather than adding a second one — appending our
           own produced "Secured checkout Checkout". Everything else Impact put
           inside (the .button__loader, any dynamic-checkout marker) is left
           exactly where it was. */
        var labelHost = [].slice.call(native.children).filter(function (el) {
          return el.tagName.toLowerCase() !== 'svg' &&
            !el.classList.contains('button__loader') &&
            (el.textContent || '').trim().length > 0;
        })[0];

        if (labelHost) {
          labelHost.classList.add('ws-cart__checkout-label');
          labelHost.textContent = label;
        } else {
          [].slice.call(native.childNodes).forEach(function (n) {
            if (n.nodeType === 3) n.remove();
          });
          var span = document.createElement('span');
          span.className = 'ws-cart__checkout-label';
          span.textContent = label;
          native.appendChild(span);
        }
      }

      var lock = svgIcon(ICON.lock, '17', '1.8');
      lock.setAttribute('data-ws-lock', '');
      native.insertBefore(lock, native.firstChild);

      var arrow = svgIcon(ICON.arrow, '17', '1.8');
      arrow.setAttribute('data-ws-arrow', '');
      native.appendChild(arrow);
    }

    return native;
  }

  /* Spec 6: the store's real enabled methods, never a static set. The markup
     comes from a Liquid <template> because payment_type_svg_tag only exists in
     Liquid; cloning it keeps the native helper as the source of truth. */
  function renderPayment(footer) {
    if (footer.querySelector('.ws-cart__payment')) return;

    var tpl = document.getElementById('ws-cart-payment');
    if (!tpl) return;

    var rowEl = document.createElement('div');
    rowEl.className = 'ws-cart__payment';
    rowEl.appendChild(tpl.content.cloneNode(true));

    if (!rowEl.childElementCount) return; // no enabled methods, no empty box
    footer.appendChild(rowEl);
  }

  function renderFooter(cart) {
    var d = drawer();
    if (!d || !cart) return;

    var footer = d.querySelector('[slot="footer"]');
    if (!footer) return;

    footer.classList.add('ws-cart', 'ws-cart__footer');
    applyTokens(footer);

    /* Impact's own totals block carries the compare-at "Discounts" row that
       spec 9 records as removed by client decision. Hidden rather than deleted
       so the node still exists if the WS layer is switched off mid-session. */
    var impactTotalTitle = footer.querySelector('.cd-total-title');
    if (impactTotalTitle) {
      var block = impactTotalTitle.closest('.v-stack');
      if (block) block.classList.add('ws-cart__superseded');
    }

    var existing = footer.querySelector('.ws-cart__totals');
    var built = buildTotals(cart);
    if (existing) existing.replaceWith(built);
    else footer.insertBefore(built, footer.firstChild);

    skinCheckout(footer);

    if (!footer.querySelector('.ws-cart__trust')) {
      var trust = buildTrustLine();
      if (trust) footer.appendChild(trust);
    }

    renderPayment(footer);
  }

  // -------------------------------------------------------------------------
  // Swap survival
  // -------------------------------------------------------------------------

  /* The scroller is NOT .cart-drawer__inner. That node has
     scrollHeight === clientHeight and overflow-y: visible — it does not scroll
     at all, and an earlier version of this pair targeted it, which made both
     functions silent no-ops.

     Impact's drawer scrolls inside its SHADOW ROOT: a div exposed as
     ::part(body) with overflow-y: auto. Measured with eleven line items —
     1709px of scroll, and scrolling it does bring the last row into view, so
     the drawer is not broken, it was simply never the element we were holding.

     Guarded because it reaches across a shadow boundary into a delivered
     component: if Impact renames the part, this degrades to Impact's own
     behaviour (scroll resets to top) rather than throwing. */
  function scroller() {
    var d = drawer();
    if (!d || !d.shadowRoot) return null;
    return d.shadowRoot.querySelector('[part~="body"]');
  }

  function captureScroll() {
    var el = scroller();
    savedScrollTop = el ? el.scrollTop : 0;
  }

  function restoreScroll() {
    var el = scroller();
    if (el && savedScrollTop > 0) el.scrollTop = savedScrollTop;
  }

  /* Impact's swap wipes our injected DOM. Observe the drawer and re-apply when
     our root is gone. A MutationObserver rather than a timer because the swap
     fires at 0ms for variant:add and 1250ms otherwise, and we cannot clear or
     read Impact's timer. */
  function observe() {
    var d = drawer();
    if (!d || observer) return;

    observer = new MutationObserver(function () {
      if (mutating) return;
      mutating = true;

      var innerEl = inner();
      /* PRE-EXISTING BUG, found while verifying the save chip and fixed here
         because it silently breaks the chip's survival past the FIRST cart
         change: innerEl itself is never replaced. theme.js:1812 reassigns its
         innerHTML in place, which destroys every child but leaves attributes
         set on innerEl itself — including the "ws-cart" class apply() adds —
         untouched. Checking that class therefore reads "already applied" as
         true forever after the first swap, so apply() (header consolidation,
         and restyleLineItems, which the chip lives in) silently stopped
         re-running on every later cart change. Confirmed live: after one
         quantity change on a real discounted line, .ws-cart__header was gone
         and Impact's own suppressed header was back.

         .ws-cart__header is a CHILD apply() inserts (:245-249 above), so it
         IS destroyed by the same innerHTML reassignment that wipes everything
         else — its absence is what actually means "Impact just swapped this
         content in", regardless of what survived on the parent. */
      if (innerEl && !innerEl.querySelector('.ws-cart__header')) {
        apply(window.wsCart.lastCart);
        restoreScroll();
      }

      /* The footer is a SEPARATE replacement target and needs its own check.
         theme.js:1817 replaces [slot="footer"] synchronously, outside the
         1250ms timer that governs .cart-drawer__inner, so the two nodes are
         wiped on different beats and neither implies the other.

         Our cart:change handler does rebuild the footer, but only because
         Impact registers its listener in connectedCallback at element upgrade
         and ours registers at init, so Impact's replacement happens first by
         accident of ordering. That is not something to rely on. Checking the
         node itself makes restoration independent of listener order.

         Without this the payment icons in particular would be present on first
         open and gone after the first quantity change, because they are cloned
         into the footer once and the footer does not survive. */
      var d = drawer();
      var footerEl = d && d.querySelector('[slot="footer"]');
      if (footerEl && !footerEl.classList.contains('ws-cart__footer')) {
        renderFooter(window.wsCart.lastCart);
      }

      mutating = false;
    });

    observer.observe(d, { childList: true, subtree: true });
  }

  // -------------------------------------------------------------------------
  // Task 3: protection cart mutations
  //
  // Both paths below replicate the theme's OWN Ajax Cart contract exactly —
  // same endpoints, same cart:prepare-bundled-sections collection, same
  // cart:change detail shape ProductForm and LineItemQuantity already use
  // (theme.js:2825-2863 for add, theme.js:2043-2085 for change) — so Impact's
  // own listener (CartDrawer._onCartChanged, theme.js:1807) performs the real
  // swap and nothing here forks it. The file's top comment says "we never
  // issue the quantity request ourselves"; that is about lines Impact already
  // renders and already has a control for. There is no such control for a fee
  // that is not in the cart yet, so adding one is the one place this
  // controller must call the cart API directly rather than proxy an existing
  // control. Removing an EXISTING protection line does reuse Impact's own
  // control (applyProtectionCard above), consistent with that rule.
  // -------------------------------------------------------------------------

  function bundledSections() {
    var sections = [];
    document.documentElement.dispatchEvent(new CustomEvent('cart:prepare-bundled-sections', {
      bubbles: true,
      detail: { sections: sections }
    }));
    return sections;
  }

  function broadcastCartChange(cart, baseEvent, selfOriginated) {
    window.wsCart.lastCart = cart;
    document.documentElement.dispatchEvent(new CustomEvent('cart:change', {
      bubbles: true,
      /* wsSelf marks OUR OWN write. Impact still handles the event and swaps
         normally; only our reconcile skips re-entering on it, so a pass cannot
         schedule a redundant follow-up of itself. */
      detail: { baseEvent: baseEvent, cart: cart, wsSelf: !!selfOriginated }
    }));
  }

  /* /cart/add.js's own response is the ADDED LINE, not the full cart (no
     items[], no item_count, no totals) — confirmed against ProductForm's own
     handling, which is why it fetches cart.js separately and grafts only the
     `sections` field from the add response onto it. Mirrored here rather than
     assumed. */
  /* One add path, shared by the protection row and the upsell rail. Both need
     exactly the same Impact contract — bundled sections on the add, a fresh
     cart fetch afterwards, then one broadcast — so it lives here once instead
     of being written twice with two chances to drift. */
  async function addVariantToCart(variantId, properties) {
    if (!variantId) throw new Error('no variant');
    var body = {
      id: variantId,
      quantity: 1,
      sections: bundledSections()
    };
    if (properties) body.properties = properties;

    var response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    });
    if (!response.ok) throw new Error('add failed');

    var added = await response.json();
    var cart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
    cart.sections = added.sections;
    broadcastCartChange(cart, 'variant:add');
    return cart;
  }



  /* Spec 5: "market gating enforced twice ... display and enforcement must
     agree." The render gate (protectionGateOpen, used by renderProtectionPrompt
     and by the empty-state check) stops the row appearing outside the
     allow-list; this actively removes a line that is already there, because a
     market switch is a full page load (scheduleLoadPass's own reasoning) and
     can land the SAME session outside a list it started inside. Reuses the
     exact endpoint and event contract LineItemQuantity itself uses
     (theme.js:2040-2088), whose own response is already a full cart object —
     unlike /cart/add.js above, no separate cart.js fetch is needed here. */
  // -------------------------------------------------------------------------
  // Cart mutation primitives (spec 1.7)
  //
  // Both take a LINE KEY, never a variant id, for anything already in the cart:
  // Shopify re-keys siblings whenever a line is removed, so a variant id is not
  // a stable handle. Neither broadcasts — reconcile broadcasts ONCE at the end
  // if anything changed (spec 1.6), so a pass cannot re-trigger itself.
  // -------------------------------------------------------------------------

  async function cartChange(key, quantity, properties) {
    var payload = { id: key, quantity: quantity, sections: bundledSections() };
    /* change.js accepts properties, which is what lets adoption set the marker
       AND clamp the quantity in a single call rather than a remove-then-add
       that would lose the line's place and its other properties. */
    if (properties) payload.properties = properties;
    var response = await fetch(window.Shopify.routes.root + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    });
    if (!response.ok) throw new Error('change failed');
    return await response.json();
  }

  async function cartAdd(variantId, properties) {
    var response = await fetch(window.Shopify.routes.root + 'cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        id: variantId, quantity: 1, properties: properties, sections: bundledSections()
      })
    });
    if (!response.ok) throw new Error('add failed');
    var added = await response.json();
    var cart = await (await fetch(window.Shopify.routes.root + 'cart.js')).json();
    cart.sections = added.sections;
    return cart;
  }

  /* Protection flags: { token, optOut, autoAdded }, STAMPED WITH THE CART TOKEN.
     On read a mismatched token discards them, so the opt-out holds across tabs
     and reloads for the life of THAT cart, and a brand-new cart starts fresh —
     a returning customer with a new cart gets the default-on behaviour again.
     A bare session key would instead carry one cart's refusal into the next.

     Storage is tiered and PROBED with a test write: localStorage (so it holds
     across tabs) → sessionStorage → an in-memory object. Private mode throws on
     access rather than returning null, so probing is the only safe test. */
  var PROT_FLAG_KEY = 'ws_cart_protection';
  var protStore = null;

  function protStorage() {
    if (protStore) return protStore;
    var probe = '__ws_probe__';
    var candidates = [];
    try { candidates.push(window.localStorage); } catch (e) { /* blocked */ }
    try { candidates.push(window.sessionStorage); } catch (e) { /* blocked */ }
    for (var i = 0; i < candidates.length; i++) {
      try {
        candidates[i].setItem(probe, '1');
        candidates[i].removeItem(probe);
        protStore = candidates[i];
        return protStore;
      } catch (e) { /* try the next one */ }
    }
    var mem = {};
    protStore = {
      getItem: function (k) { return mem[k] !== undefined ? mem[k] : null; },
      setItem: function (k, v) { mem[k] = String(v); },
      removeItem: function (k) { delete mem[k]; }
    };
    return protStore;
  }

  function cartToken() {
    return (window.wsCart && window.wsCart.lastCart && window.wsCart.lastCart.token) || '';
  }

  function protFlags() {
    var token = cartToken();
    try {
      var raw = protStorage().getItem(PROT_FLAG_KEY);
      var f = raw ? JSON.parse(raw) : null;
      if (f && f.token === token) return f;
    } catch (e) { /* unreadable or malformed: fall through to defaults */ }
    return { token: token, optOut: false, autoAdded: false };
  }

  function setProtFlags(patch) {
    var f = protFlags();
    for (var k in patch) { if (Object.prototype.hasOwnProperty.call(patch, k)) f[k] = patch[k]; }
    f.token = cartToken();
    try { protStorage().setItem(PROT_FLAG_KEY, JSON.stringify(f)); } catch (e) { /* degrade */ }
  }

  function protectionOptedOut() { return !!protFlags().optOut; }
  function setProtectionOptedOut(on) { setProtFlags({ optOut: !!on }); }

  /* A protection add that failed is remembered for the same reason a gift one
     is: no retry loop. Cleared on the next external cart event. */
  var protAddFailed = false;

  function isMarkedProtection(line) {
    return !!(line && (line.properties || {})._ws_protection);
  }

  // -------------------------------------------------------------------------
  // Gift engine + integrity validator (spec 4)
  //
  // The gift is a zero-priced product with a real URL, so it is directly
  // addable by anyone. Presence-checking is not enough: a crafted
  // /cart/change.js can set qty 50, and a forged _ws_gift property is trivial.
  // Every pass therefore re-derives what SHOULD be there and removes anything
  // else. Client-side enforcement only — strong against normal use and scripted
  // Cart-API abuse, not against a JS-disabled crafted checkout. The mitigation
  // for that is the gift staying a single low-value, unmerchandised product.
  // -------------------------------------------------------------------------

  /* WHICH TIERS ARE LIVE — used to decide what SHOULD be in the cart. */
  function giftTierByVariant() {
    var map = {};
    ((config && config.tiers) || []).forEach(function (t) {
      if (t.type === 'gift' && t.giftVariantId) map[String(t.giftVariantId)] = t;
    });
    return map;
  }

  /* WHICH PRODUCTS ARE GIFTS — a different question, answered from
     config.giftIdentity, which Liquid emits for all three slots regardless of
     enabled state. Built from tiers instead, the validator would go blind to a
     gift whose tier is switched off today and last month's gift could be
     direct-added from its product page and survive every pass. */
  function giftIdentity() {
    var ids = (config && config.giftIdentity) || [];
    var variants = {}, products = {};
    ids.forEach(function (g) {
      if (g && g.variantId) variants[String(g.variantId)] = true;
      if (g && g.productId) products[String(g.productId)] = true;
    });
    return { variants: variants, products: products };
  }

  /* A gift add that fails is remembered, so a gift that went out of stock
     between render and reconcile cannot drive an add loop, one attempt per
     pass, forever. Cleared on the next EXTERNAL cart event, which is the point
     at which stock may plausibly have changed. */
  var giftAddFailed = {};

  async function reconcileGifts(budget) {
    var byVariant = giftTierByVariant();
    var identity = giftIdentity();
    if (!Object.keys(byVariant).length && !Object.keys(identity.variants).length) return false;

    var tiers = evaluateTiers(window.wsCart.lastCart);
    var met = {};
    tiers.forEach(function (t) { if (t.type === 'gift') met[t.id] = !!t.reached; });

    var changed = false;
    var kept = {};

    /* One mutation, then re-walk from the fresh cart: removing a line re-keys
       its siblings, so the rest of this list is stale the moment we act. */
    var again = true;
    while (again && budget.left > 0) {
      again = false;
      var items = (window.wsCart.lastCart.items) || [];
      for (var i = 0; i < items.length; i++) {
        var it = items[i];
        var tier = byVariant[String(it.variant_id)];

        /* A configured gift VARIANT with no marker — direct-added from its
           product page. Caught by identity, not by the live tier list. */
        var giftVariant = !!identity.variants[String(it.variant_id)];

        /* An INTRINSICALLY free line of a gift product: original_price zero,
           i.e. the product is priced at 0 in admin. Deliberately NOT
           final_price — a BOGO allocation zeroes final_price on a normally
           priced line, and treating that as an intrinsically-free gift would
           delete a line the customer genuinely bought. A paid sibling variant
           of the same product is spared for the same reason. */
        var strayFree = !tier && !giftVariant &&
          identity.products[String(it.product_id)] &&
          (it.original_price || 0) === 0;

        /* A marker forged onto an unrelated PAID product falls through both
           tests and is left alone on purpose: the customer paid for it, and
           deleting a purchase is worse than tolerating a stray property. */
        if (!tier && !giftVariant && !strayFree) continue;

        var marker = (it.properties || {})._ws_gift;
        var valid = !!tier && marker === tier.id && met[tier.id] && !kept[tier.id];

        if (!valid) {
          window.wsCart.lastCart = await cartChange(it.key, 0);
          changed = true; budget.left--; again = true; break;
        }
        if (it.quantity > 1) {
          window.wsCart.lastCart = await cartChange(it.key, 1);
          changed = true; budget.left--; again = true; break;
        }
        kept[tier.id] = true;
      }
    }

    /* Add any met gift tier that has no valid line. evaluateTiers already
       requires a paid line and an available gift variant, so a gift can never
       land alone or out of stock. */
    for (var j = 0; j < tiers.length && budget.left > 0; j++) {
      var t = tiers[j];
      if (t.type !== 'gift' || !t.giftVariantId || !t.reached || kept[t.id]) continue;
      if (giftAddFailed[t.id]) continue; // remembered failure: do not retry-loop
      try {
        window.wsCart.lastCart = await cartAdd(t.giftVariantId, { _ws_gift: t.id });
        kept[t.id] = true;
        changed = true; budget.left--;
      } catch (e) {
        giftAddFailed[t.id] = true; // e.g. sold out since the page rendered
      }
    }

    return changed;
  }

  // -------------------------------------------------------------------------
  // Protection: ensure / opt-out / clamp (spec 5)
  // -------------------------------------------------------------------------

  function protectionLines() {
    return ((window.wsCart.lastCart && window.wsCart.lastCart.items) || [])
      .filter(isProtectionLine);
  }

  async function reconcileProtection(budget) {
    var p = (config && config.protection) || {};
    var changed = false;
    var lines = protectionLines();

    /* 1. Gate closed (setting off, no product, or outside the country
       allow-list) or nothing paid left to protect — strip every protection
       line. "Never alone" and the market gate are the same removal. */
    if (!protectionGateOpen() || !hasPaidLine(window.wsCart.lastCart)) {
      while (lines.length && budget.left > 0) {
        window.wsCart.lastCart = await cartChange(lines[0].key, 0);
        changed = true; budget.left--;
        lines = protectionLines();
      }
      return changed;
    }

    /* 2. Duplicates: drop one, PREFERRING TO KEEP THE MARKED line. Removing
       blindly can keep an unmarked legacy line and discard the one the engine
       owns, which then has to be adopted all over again next pass. */
    while (lines.length > 1 && budget.left > 0) {
      var unmarked = lines.filter(function (l) { return !isMarkedProtection(l); });
      var victim = unmarked.length ? unmarked[0] : lines[lines.length - 1];
      window.wsCart.lastCart = await cartChange(victim.key, 0);
      changed = true; budget.left--;
      lines = protectionLines();
    }

    /* 3. ADOPT an unmarked line rather than stripping it. Carts persist across
       deploys: a line added by an old protection app, from the product page, or
       by a pre-marker build carries no property. Stripping it would delete
       something the customer already agreed to pay for; leaving it unmarked
       makes the engine unable to tell it apart from a stray. One change.js call
       sets the marker and clamps quantity together, preserving whatever other
       properties the line already had. */
    if (lines.length === 1 && !isMarkedProtection(lines[0]) && budget.left > 0) {
      var existing = lines[0].properties || {};
      var props = {};
      for (var k in existing) {
        if (Object.prototype.hasOwnProperty.call(existing, k)) props[k] = existing[k];
      }
      props._ws_protection = '1';
      window.wsCart.lastCart = await cartChange(lines[0].key, 1, props);
      changed = true; budget.left--;
      lines = protectionLines();
    }

    /* 4. Exactly one unit. Without this a one-line crafted change.js charges
       the fee times fifty. */
    if (lines.length === 1 && lines[0].quantity !== 1 && budget.left > 0) {
      window.wsCart.lastCart = await cartChange(lines[0].key, 1);
      changed = true; budget.left--;
      lines = protectionLines();
    }

    /* 5. Pre-select, ONCE per cart. config.protection.defaultOn is already
       forced false in Liquid unless the merchant named an explicit country
       allow-list, so this cannot pre-tick a paid add-on for an EU buyer.

       autoAdded is written only AFTER Shopify confirms the add — writing it
       optimistically would burn the single auto-add on a network hiccup. It is
       also what stops a re-add when the buyer removed protection somewhere the
       drawer's own Remove listener never saw, such as the /cart page. */
    var flags = protFlags();
    if (!lines.length && p.defaultOn && p.variantId &&
        !flags.optOut && !flags.autoAdded && !protAddFailed && budget.left > 0) {
      try {
        window.wsCart.lastCart = await cartAdd(p.variantId, { _ws_protection: '1' });
        setProtFlags({ autoAdded: true });
        changed = true; budget.left--;
      } catch (e) {
        protAddFailed = true;
      }
    }

    return changed;
  }



  // -------------------------------------------------------------------------
  // Reconcile (spec 1) — skeleton. Gift, validator and protection phases land
  // with their modules; the engine's guarantees are established here so those
  // phases plug into a pass that already queues, guards and refetches.
  //
  // WHAT THE GIFT-LINE VALIDATOR IS, AND WHAT IT IS NOT
  //
  // Spec 4 calls it "a security control". Read that narrowly. It runs in
  // JavaScript, in the buyer's browser, which makes it a UX control and not a
  // security boundary. Anyone who POSTs the gift variant to /cart/add.js and
  // navigates straight to /checkout never executes a line of it, and no amount
  // of care in the implementation changes that. The bounded loops, the
  // duplicate collapsing and the quantity clamp all raise the effort required
  // for casual and scripted abuse. None of them make the gift unobtainable.
  //
  // Two claims that sound alike and are not:
  //   "we validate gift lines"                                   — true
  //   "gift lines cannot be obtained without earning them"       — false
  // Only write the first one down anywhere.
  //
  // Real enforcement means server-side evaluation, which on Shopify is a
  // Function. That is outside this build and outside the quote, and it is not
  // being recommended here: the gift is a low-value, inventory-limited,
  // unmerchandised product, so client-side is proportionate to what is at risk.
  // This is a written decision rather than an assumption, so that nobody later
  // reads the validator and believes the stronger claim.
  //
  // Spec 4's own disclosed limitation says the same thing in fewer words:
  // "strong against normal use and scripted Cart-API abuse, not against
  // JS-disabled crafted checkouts."
  // -------------------------------------------------------------------------

  function scheduleReconcile() {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(runReconcile, RECONCILE_DEBOUNCE_MS);
  }

  async function runReconcile() {
    if (mutating) {
      passQueued = true; // spec 1.3: exactly one follow-up, no interleaving
      return;
    }

    mutating = true;
    try {
      /* Spec 1.4: always refetch fresh at the start of the pass. Never seed from
         event.detail.cart — Shopify re-keys and splits lines between passes, so a
         stale seed produces wrong line keys. This is not the redundant second
         trip the jQuery handler made; it fetches state that did not exist when
         the earlier response was written. */
      var response = await fetch(window.Shopify.routes.root + 'cart.js');
      if (!response.ok) return;
      window.wsCart.lastCart = await response.json();

      /* Spec 1.5 order of operations: gift add/remove, then the integrity
         validator strip, then protection ensure/opt-out/clamp. Runs before the
         totals/rewards/footer calls below so a line about to be stripped is
         never counted in what they display.

         BOUNDED (spec 1.7). A scripted attacker can add lines faster than a
         fixed single pass removes them, so the pass spends a budget rather than
         looping until clean, and queues one more pass if it runs out. Bounded
         work plus a forced follow-up beats an unbounded loop that can hang the
         drawer. */
      var budget = { left: ACTION_BUDGET };
      var mutated = false;
      try {
        if (await reconcileGifts(budget)) mutated = true;
        if (await reconcileProtection(budget)) mutated = true;
      } catch (e) {
        /* Spec 0.6: degrade silently, the next pass tries again. */
      }
      if (budget.left <= 0) passQueued = true; // residue remains — force another pass

      /* Spec 1.6: broadcast ONCE, only if something changed, marked
         self-originated so our own write does not schedule another reconcile. */
      if (mutated) broadcastCartChange(window.wsCart.lastCart, 'line-item:change', true);

      updateCount(wsVisibleCount(window.wsCart.lastCart));
      renderRewards(window.wsCart.lastCart);
      renderBogoNoteInDrawer(window.wsCart.lastCart);
      renderFooter(window.wsCart.lastCart);

      /* The save chip is line-scoped and, per the timing note above, normally
         rides Impact's swap via apply(). But THIS pass is the one case with no
         swap to ride: scheduleLoadPass() runs it on page load precisely
         because there may be no cart:change event at all (spec 5's market
         switch case), and on load the DOM already holds Impact's
         server-rendered lines with no swap coming. Without this, a line that
         already carries a real discount when the page loads shows no chip
         until the buyer changes the cart — confirmed against a real line with
         a live "Buy 1, Get 1 FREE" allocation. restyleLineItems is idempotent
         (removes and rebuilds its own chip each call), so re-running it here
         and again from apply() on the next swap is safe. */
      var innerEl = inner();
      if (innerEl) restyleLineItems(innerEl, window.wsCart.lastCart);
    } catch (e) {
      /* Spec 0.6: graceful degradation, never a console error. */
    } finally {
      mutating = false;
      if (passQueued) {
        passQueued = false;
        scheduleReconcile();
      }
    }
  }

  // -------------------------------------------------------------------------
  // Wiring
  // -------------------------------------------------------------------------

  function onCartChange(event) {
    intentSeq += 1;
    captureScroll();

    var cart = event && event.detail && event.detail.cart;
    if (cart) {
      window.wsCart.lastCart = cart;
      /* Immediate, matching Impact's synchronous footer replacement. Both of
         these are totals-derived; line-scoped content rides the swap. */
      updateCount(wsVisibleCount(cart));
      renderRewards(cart);
      renderBogoNoteInDrawer(cart);
      /* Impact replaces [slot="footer"] synchronously, outside its 1250ms
         timer, so our footer has to be rebuilt on the same beat or it is
         briefly Impact's again. */
      renderFooter(cart);

      /* THE CART PAGE IS DRIVEN FROM HERE, not from apply().

         apply() is reached through the MutationObserver on the drawer's inner
         element, which fires because Impact re-renders the drawer on every cart
         change. Impact does NOT re-render the cart page, so on /cart that
         observer never fires and applyCartPage was never reached — which is why
         emptying the cart there left the rows, the rewards bar and the totals of
         a cart that no longer existed on screen.

         Kept BEFORE the wsSelf return below: the gift engine's own removals are
         broadcast as wsSelf, and those are exactly the changes the cart page
         must react to. */
      try { applyCartPage(cart); } catch (e) { /* never break the drawer */ }
    }

    /* Our own broadcast has already reconciled; re-entering would spend a whole
       extra pass confirming what this one just did. */
    if (event && event.detail && event.detail.wsSelf) return;

    /* An external cart event is the moment stock may plausibly have changed, so
       a gift whose add failed earlier gets exactly one more attempt. */
    giftAddFailed = {};
    protAddFailed = false;

    scheduleReconcile();
  }

  function onCartRefresh() {
    intentSeq += 1;
    captureScroll();
    scheduleReconcile();
  }

  /* A pass on load, not only on cart events. Two things depend on it and both
     were broken without it: spec 5 auto-adds protection "the first time
     eligible on page load", and the gift-line validator runs "every pass" —
     neither can happen if the first pass waits for a cart change that may
     never come. A market switch is the case with no cart event to fall back
     on: Impact's country selector is a plain type="submit" inside
     {% form 'localization' %} with no interception anywhere in theme.js, so
     changing country is a native POST and a full page load.

     But the pass refetches by definition (spec 1.4), so running it on every
     page view would put a GET /cart.js on the whole site, and performance is
     the stated priority on this build. Two gates:

     1. Skip entirely on an empty cart. Liquid resolved that at render time, so
        the majority of sessions — browse pages, nothing in the cart — pay
        nothing at all, no JS and no request.
     2. Never on the critical path. The pass reconciles state that changed
        outside this tab, so nothing about it is urgent. It waits for load and
        then for idle. */
  function scheduleLoadPass() {
    if (config.cartEmpty) return;

    var start = function () {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(scheduleReconcile, { timeout: 3000 });
      } else {
        setTimeout(scheduleReconcile, 1000);
      }
    };

    if (document.readyState === 'complete') start();
    else window.addEventListener('load', start, { once: true });
  }

  function init() {
    config = readConfig();
    if (!config || !config.enabled) return;

    /* Nothing on window unless ws-prefixed. */
    window.wsCart = { config: config, lastCart: null };

    /* We do not push a section id on cart:prepare-bundled-sections. This section
       renders only the config blob, which is cart-independent, so it never needs
       re-rendering. Any WS module that later needs server-rendered Liquid per
       cart change must push its id there or it will never receive HTML. */

    /* Capture, so it runs before anything the delivered theme may bind later. */
    document.addEventListener('click', onRemoveClick, true);

    /* CAPTURE, and it must stay capture. Impact's badge listener
       (theme.js:1213) is a bubble listener on document, and theme.js binds it
       before this file runs — so on every cart change Impact would reach the
       badge first, holding the raw item_count while this layer still had the
       PREVIOUS cart in hand. The interception would then be unable to match the
       number and would fall back to a network round trip for a cart already
       sitting in the event detail. Recording it one phase earlier removes that
       race and that fetch. */
    document.addEventListener('cart:change', function (event) {
      var c = event && event.detail && event.detail.cart;
      if (c) window.wsCart.lastCart = c;
    }, true);

    document.addEventListener('cart:change', onCartChange);
    document.addEventListener('cart:refresh', onCartRefresh);

    /* Drawn from Liquid's own copy of the cart, in the same tick — no network,
       so the very first paint already has variant lines, discount chips and the
       upsell rail. apply(null) is the fallback for the case where the blob
       carries no cart at all. */
    var seed = config.initialCart && config.initialCart.items ? config.initialCart : null;
    if (seed) window.wsCart.lastCart = seed;
    apply(seed);
    observe();

    /* Claim the badge before any cart change can reach it. With a seed the
       count is stamped outright; without one the elements are only claimed, so
       the server's own number stays on screen until primeCart() returns rather
       than being replaced with a guess of zero. */
    if (seed) {
      stampCartCounts(wsVisibleCount(seed));
    } else {
      var counts = document.querySelectorAll('cart-count');
      for (var ci = 0; ci < counts.length; ci++) claimCartCount(counts[ci]);
    }

    primeCart();
    scheduleLoadPass();
  }

  /* The first paint with real data, started AT ONCE.

     Everything cart-shaped in the drawer used to wait for scheduleLoadPass,
     which waits for window.load and then an idle callback with a 3000ms
     timeout. On a fast connection that is imperceptible. On a slow one it is
     seconds of a drawer with no variant lines, no discount chips and no upsell
     rail — the state that reads as the old cards.

     This is one GET of the cart, issued at DOMContentLoaded, purely to draw
     with. It does not reconcile: the gift and protection engines still run in
     scheduleLoadPass, deliberately, so nothing is MUTATED during first paint.
     Drawing early and mutating late is the whole point of splitting them. */
  /* ---------------------------------------------------------------------
     THE CART PAGE.

     /cart is a second surface showing the same cart, and until now it got none
     of this: Impact's default table, no rewards bar, no upsell, no protection,
     no discount row, and the BOGO shown as red "BUY 1, GET 1 FREE" text. The
     reference ports its whole layer here; ours did not.

     It is cheap because the markup is the SAME. Impact renders cart-page rows
     with `line-item`, .title-cart-drawer, .quan-button-group,
     .quantity-input[data-line-key] and .line-item-pricing — the exact elements
     restyleLineItems already understands — and each row's line-item is
     self-contained, media through remove control. So the restyler is reused
     verbatim rather than reimplemented.

     What differs is the FRAME. The drawer is one scrolling column; the cart page
     is a <table>, and a rewards bar cannot be appended into a <tbody>. So the
     modules go around the table:

         div.cart-order__summary
           [ rewards bar ]        <- inserted before
           table.order-summary    <- rows restyled in place
           [ upsell, protection ] <- appended after
         div.cart-form__totals    <- band and gift line

     The drawer path is untouched: this runs only against elements inside the
     cart section, and its caller wraps it so a failure here cannot reach the
     drawer. */
  function cartPageHosts() {
    var section = document.querySelector('[id^="shopify-section-template"][id*="main"]');
    if (!section) return null;
    var summary = section.querySelector('.cart-order__summary');
    var body = section.querySelector('tbody.order-summary__body');
    if (!summary || !body) return null;
    return { section: section, summary: summary, body: body,
             table: section.querySelector('table.order-summary'),
             totals: section.querySelector('.cart-form__totals') };
  }

  /* The cart page is SERVER-rendered and Impact does not re-render it on a
     cart change the way it re-renders the drawer. So when the gift engine adds
     the gift — which happens after the page has already been sent — the line
     exists in the cart but there is no row for it, and the shopper only sees it
     if they happen to reload.

     This pulls the section's own HTML back from Shopify and swaps the rows in.
     Guarded three ways because it is a network call inside a render pass:
     only when the row count actually disagrees with the cart, never twice at
     once, and never as a loop — the swap is followed by one restyle, not by
     another pass through here. */
  var cartPageSyncing = false;
  function refreshCartPageRows(h, cart) {
    if (cartPageSyncing) return;
    var expected = ((cart && cart.items) || []).length;
    var actual = h.body.querySelectorAll('line-item.line-item').length;
    if (expected === actual) return;

    /* EMPTY IS THE CASE THAT MATTERS MOST, and the first version of this guard
       excluded it: `if (!expected)` returned early on a zero-length cart, so
       removing the last product left the page showing the rows, the rewards bar
       and the totals of a cart that no longer existed — including a free gift
       the engine had already taken back.

       An emptied cart replaces the WHOLE section rather than just the rows,
       because there is no table in Shopify's empty state to swap a tbody into,
       and every module we appended has to leave with it. */
    var emptied = expected === 0;

    cartPageSyncing = true;
    var id = h.section.id.replace('shopify-section-', '');
    fetch(window.Shopify.routes.root + 'cart?section_id=' + encodeURIComponent(id),
          { headers: { 'Accept': 'text/html' } })
      .then(function (r) { return r.ok ? r.text() : null; })
      .then(function (html) {
        if (!html) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        var live = cartPageHosts();

        if (emptied) {
          var freshSection = doc.body.firstElementChild;
          var liveSection = document.getElementById(h.section.id);
          if (freshSection && liveSection) liveSection.innerHTML = freshSection.innerHTML;
          return;                       // nothing left to restyle
        }

        var fresh = doc.querySelector('tbody.order-summary__body');
        if (!fresh || !live) return;
        live.body.replaceWith(fresh);
        var again = cartPageHosts();
        /* Restyle the rows that just arrived. Modules are left alone — they are
           outside the table and were not replaced. */
        if (again) restyleLineItems(again.body, cart, again.summary);
      })
      .catch(function () { /* the rows stay as they are; a reload fixes it */ })
      .finally(function () { cartPageSyncing = false; });
  }

  function applyCartPage(cart) {
    var h = cartPageHosts();
    if (!h) return;

    /* Every rule in ws-cart.css hangs off .ws-cart, so the frame carries it and
       the rows inherit the whole design with no page-specific CSS. */
    h.summary.classList.add('ws-cart', 'ws-cart--page');

    /* The rows, through the same restyler the drawer uses. Modules are sent to
       the summary div rather than the tbody, because they cannot live in a table. */
    restyleLineItems(h.body, cart, h.summary);

    /* If the engine has added or removed a line since this page was rendered,
       bring the rows back into agreement with the cart. */
    refreshCartPageRows(h, cart);

    var oldBar = h.summary.querySelector('.ws-rewards');
    var bar = buildRewardsBar(cart);
    if (bar) {
      if (oldBar) oldBar.replaceWith(bar);
      else h.summary.insertBefore(bar, h.table || h.summary.firstChild);
    } else if (oldBar) {
      oldBar.remove();
    }

    /* The BOGO note, on the page as well as in the drawer. This surface builds
       its rewards bar directly rather than through renderRewards, so it needs
       its own call — and its own anchors: there is no .ws-cart__header here,
       and with no tiers configured there is no bar either, in which case the
       note goes in front of the table so it still precedes the line items. */
    renderBogoNote(cart, h.summary,
      h.summary.querySelector('.ws-rewards') || null);
    if (!h.summary.querySelector('.ws-rewards')) {
      var pageNote = h.summary.querySelector('.ws-bogo-note');
      if (pageNote && h.table && pageNote.compareDocumentPosition(h.table)
          & Node.DOCUMENT_POSITION_PRECEDING) {
        h.summary.insertBefore(pageNote, h.table);
      }
    }

    /* The same band the drawer draws, so the BOGO saving is visible as an Item
       Discounts row instead of silently reducing the subtotal. */
    if (h.totals) {
      h.totals.classList.add('ws-cart');
      var oldTotals = h.totals.querySelector('.ws-cart__totals');
      var totals = buildTotals(cart);
      if (totals) {
        if (oldTotals) oldTotals.replaceWith(totals);
        else h.totals.insertBefore(totals, h.totals.firstChild);
      }
    }
  }

  function primeCart() {
    var reveal = function () {
      var host = drawer();
      if (host) host.classList.add('ws-cart-ready');
    };
    /* Already drawn from the Liquid seed, so the drawer is on screen and
       correct; this fetch is now only a correctness pass against the live cart
       (a back/forward restore can hand us a stale blob). It must never gate the
       reveal — measured at over 30s on a throttled connection. */
    reveal();
    if (config.cartEmpty) return;
    fetch(window.Shopify.routes.root + 'cart.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (cart) {
        if (cart) {
          window.wsCart.lastCart = cart;
          apply(cart);           // reveals through the branch above
        } else {
          reveal();
        }
      })
      .catch(reveal);            // network refused it: show what we have
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
